import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import MasksOutlinedIcon from '@mui/icons-material/MasksOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import GhostButton from '../../../../../components/common/buttons/GhostButton';
import BorderedButton from '../../../../../components/common/buttons/BorderedButton';
import CopyToClipboard from '../../../../../components/common/CopyToClipboard';
import TableCard, { TableColumn } from '../../../../../components/common/TableCard';
import { useThemeContext } from '../../../../../contexts/BaseThemeContextProvider';
import AddVitalsForm from '../../../../../frost/AddVitalsForm';
import useBackendApi from '../../../../../hooks/useBackendApi';
import useSnackBarAlertMessages from '../../../../../hooks/useSnackBarAlertMessages';
import {
  AccountEvent,
  AccountEventGuest,
  EventForm,
  EventRequirement,
  EventRequirementFormOption,
  EventRequirementTestOption,
  OrganizationStaffMember,
} from '../../../../../types/account';
import { ClearanceStatus, EventEmployeeClearanceStatus } from '../../../../../types/api/accountEvents';
import { formatDateTime, formatEventDateTime } from '../../../../../utils/dateTime';
import { renderText } from '../../../../../utils/ui-text';

import {
  checkEventClearanceReportAvailability,
  downloadEventClearanceReport,
  downloadGuestClearanceReport,
  fetchAccountEventAssignedStaff,
  fetchAccountEventForms,
  fetchAccountEventMetadata,
  fetchEventClearanceStatuses,
  fetchEventRequirementForms,
  fetchEventRequirementTests,
  ServiceRequestsMetadata,
  updateAccountEventMetadata,
} from './eventSupportApi';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

const CLEARANCE_CHIP_CONFIG: Record<ClearanceStatus, { color: 'default' | 'info' | 'success' | 'warning'; label: string }> = {
  NOT_EVALUATED: { color: 'default', label: 'Not evaluated' },
  PENDING: { color: 'info', label: 'Pending' },
  PROCESSING: { color: 'info', label: 'Processing' },
  UNABLE_TO_DETERMINE: { color: 'warning', label: 'Unable to determine' },
  CLEARED: { color: 'success', label: 'Cleared' },
  RESTRICTED: { color: 'warning', label: 'Restricted' },
};

function GuestClearanceChip({
  guest,
  orgPathName,
  accPathName,
  eventId,
  addAlertMessages,
}: {
  guest: AccountEventGuest;
  orgPathName: string;
  accPathName: string;
  eventId: number;
  addAlertMessages: (message: any) => void;
}) {
  const { apiFetchRequest } = useBackendApi();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);

  const clearanceStatus = guest.clearance_status;
  const chipConfig = clearanceStatus ? CLEARANCE_CHIP_CONFIG[clearanceStatus] : null;

  if (!chipConfig) {
    return null;
  }

  const guestId = guest.user_id || guest.patient_id;
  const isClickable =
    (clearanceStatus === 'CLEARED' || clearanceStatus === 'RESTRICTED') && Boolean(guestId);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isClickable || isDownloading || !guestId) return;

    setIsDownloading(true);
    try {
      const result = await downloadGuestClearanceReport({
        accPathName,
        apiFetchRequest,
        eventId,
        guestUserId: guestId,
        clearanceStatus: clearanceStatus === 'CLEARED' ? 'Cleared' : 'Restricted',
        fail: () => {
          addAlertMessages({ severityType: 'error', message: 'Failed to download clearance report' });
        },
        navigate,
        orgPathName,
      });

      if (result.status === 'success') {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        const ext = 'pdf';
        const prefix = clearanceStatus === 'CLEARED' ? 'Clearance' : 'Referral';
        a.download = `${prefix}-${guest.full_name?.replace(/\s+/g, '-') || guestId}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else if (result.status === 'not-found') {
        addAlertMessages({ severityType: 'error', message: 'Clearance report not found or unavailable' });
      }
    } catch (e) {
      addAlertMessages({ severityType: 'error', message: 'An error occurred during download' });
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isClickable) {
    return <Chip color={chipConfig.color} label={chipConfig.label} size="small" sx={{ flexShrink: 0 }} />;
  }

  return (
    <Chip
      color={chipConfig.color}
      label={isDownloading ? 'Downloading...' : chipConfig.label}
      size="small"
      onClick={handleClick}
      clickable
      sx={{ flexShrink: 0 }}
      icon={isDownloading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
    />
  );
}

type RequirementDialogSelection = {
  guest: AccountEventGuest;
  requirement: EventRequirement;
};

type RetakeReasonSelection = RequirementDialogSelection & {
  reason: string;
  relationshipLabel: string;
};

type EmployeeImportPreview = {
  preview_token: string;
  classification: {
    classification_status: string;
    confidence_score: number;
    detected_title?: string | null;
  };
  document_completeness: 'COMPLETE' | 'PARTIAL' | 'UNCERTAIN';
  missing_expected_sections: string[];
  missing_question_ranges: string[];
  warnings: string[];
  employee: {
    first_name?: string | null;
    last_name?: string | null;
    full_name?: string | null;
    birthdate?: string | null;
    phone_number?: string | null;
    gender?: string | null;
    email?: string | null;
  };
};

type EmployeeImportValues = {
  firstName: string;
  lastName: string;
  birthdate: string;
  phoneNumber: string;
  gender: '' | 'male' | 'female' | 'unknown';
  email: string;
};

type EmployeeImportFieldErrors = Partial<Record<keyof EmployeeImportValues, string>>;

type EmployeeImportErrorAction = 'choose-file' | 'retry-preview' | 'reprocess' | null;

const MAX_EMPLOYEE_IMPORT_FILE_BYTES = 10 * 1024 * 1024;

const EMPTY_EMPLOYEE_IMPORT_VALUES: EmployeeImportValues = {
  firstName: '',
  lastName: '',
  birthdate: '',
  phoneNumber: '',
  gender: '',
  email: '',
};

const isEmployeeImportAccountIdentityConflict = (status: number | undefined, detail: unknown): boolean =>
  status === 409 &&
  typeof detail === 'string' &&
  detail.toLowerCase().includes('account with this email');

const getEmployeeImportError = (
  status: number | undefined,
  detail: unknown,
  phase: 'preview' | 'confirm',
): { action: EmployeeImportErrorAction; message: string } => {
  if (status === 401) {
    return {
      action: null,
      message: 'Your session has expired. Refresh the page and sign in again before continuing.',
    };
  }
  if (status === 403) {
    return {
      action: null,
      message: 'Only organization administrators can add an employee from a respiratory form.',
    };
  }
  if (status === 404) {
    return {
      action: null,
      message: 'This event is no longer available. Refresh the page before trying again.',
    };
  }
  if (status === 413) {
    return {
      action: 'choose-file',
      message: 'This PDF is larger than 10 MB. Choose a smaller PDF.',
    };
  }
  if (phase === 'preview' && status === 400) {
    return {
      action: 'choose-file',
      message: 'This file could not be read as a valid, non-empty PDF. Choose another PDF.',
    };
  }
  if (phase === 'preview' && status === 422) {
    return {
      action: 'choose-file',
      message: 'This file was not recognized as a Respiratory Protection Medical Form. Choose the correct form.',
    };
  }
  if (phase === 'preview' && status === 502) {
    return {
      action: 'retry-preview',
      message: 'The document extraction service is temporarily unavailable. Try extracting the form again.',
    };
  }
  if (phase === 'confirm' && status === 409 && typeof detail === 'string' && detail.includes('Preview expired')) {
    return {
      action: 'reprocess',
      message: 'This preview has expired. Extract the form again, review the details, and then confirm.',
    };
  }
  if (phase === 'confirm' && isEmployeeImportAccountIdentityConflict(status, detail)) {
    return {
      action: null,
      message:
        'An account already uses this email, but its name, birthdate, or phone number does not match. Verify the email and employee details before trying again.',
    };
  }
  if (phase === 'confirm' && status === 409 && typeof detail === 'string' && detail.includes('Multiple patient')) {
    return {
      action: null,
      message: 'Multiple patient records match these details. Contact support before creating this employee.',
    };
  }
  if (phase === 'confirm' && status === 409 && typeof detail === 'string' && detail.includes('patient')) {
    return {
      action: null,
      message:
        'An existing patient record conflicts with these details. Verify the employee information or contact support before continuing.',
    };
  }
  if (phase === 'confirm' && status === 422) {
    return {
      action: null,
      message:
        typeof detail === 'string'
          ? detail
          : 'One or more employee details are invalid. Review each field and try again.',
    };
  }
  if (phase === 'confirm' && status === 409) {
    return {
      action: null,
      message:
        'The employee could not be fully added because the account or event configuration changed. Refresh the event before retrying.',
    };
  }
  if (phase === 'confirm' && status === 503) {
    return {
      action: null,
      message:
        'The account was created, but event enrollment did not finish. You can safely select Confirm and Create again.',
    };
  }

  return {
    action: phase === 'preview' ? 'retry-preview' : null,
    message:
      phase === 'preview'
        ? 'We could not extract details from this PDF. Check your connection and try again.'
        : 'We could not confirm whether the employee was added. Refresh the employee list before trying again.',
  };
};

const validateEmployeeImportValues = (values: EmployeeImportValues): EmployeeImportFieldErrors => {
  const errors: EmployeeImportFieldErrors = {};
  if (!values.firstName.trim()) errors.firstName = 'First name is required.';
  if (!values.lastName.trim()) errors.lastName = 'Last name is required.';

  const birthdate = values.birthdate.trim();
  const parsedBirthdate = /^\d{4}-\d{2}-\d{2}$/.test(birthdate) ? new Date(`${birthdate}T00:00:00Z`) : null;
  if (
    !parsedBirthdate ||
    Number.isNaN(parsedBirthdate.getTime()) ||
    parsedBirthdate.toISOString().slice(0, 10) !== birthdate ||
    parsedBirthdate > new Date()
  ) {
    errors.birthdate = 'Enter a valid birthdate that is not in the future.';
  }

  const phoneDigits = values.phoneNumber.replace(/\D/g, '');
  if (!(phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith('1')))) {
    errors.phoneNumber = 'Enter a valid 10-digit US phone number.';
  }
  if (!values.gender) errors.gender = 'Select a gender value.';

  const email = values.email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Enter a valid email address.';
  }
  return errors;
};

const getEmployeeImportUploadWarning = (uploadStatus: string): string => {
  const messages: Record<string, string> = {
    INTERNAL_ERROR: 'an unexpected error occurred while attaching the PDF',
    INVALID_STATUS: 'the respiratory requirement is no longer pending',
    NO_REQUIREMENT_FOUND: 'no respiratory form requirement is configured for this event',
    REQUIREMENTS_LOOKUP_FAILED: 'the employee requirements could not be loaded',
    STORAGE_FOLDER_NOT_FOUND: 'the employee document folder is not available',
    UPLOAD_FAILED: 'the PDF upload failed',
  };
  return messages[uploadStatus] || `the attachment step returned ${uploadStatus}`;
};

const getServiceCountWarning = (serviceCountStatus: unknown): string | null => {
  const status = typeof serviceCountStatus === 'string' ? serviceCountStatus : '';
  const messages: Record<string, string> = {
    INVALID_EVENT_LINK: 'The file was saved, but its attendee/event link must be corrected before it can count.',
    INVALID_SERVICE_METADATA: 'The file was saved, but the event service configuration must be corrected before it can count.',
    NOT_APPLIED_EVENT_COMPLETED: 'The file was saved, but this completed event’s finalized counts were not changed.',
    PROCESSING_FAILED: 'The file was saved, but result processing failed. It can be retried using the saved source.',
    SERVICE_NOT_FOUND: 'The file was saved, but this event does not request the matching service.',
    UNMATCHED: 'The file was saved, but it was not confidently identified as a countable result.',
  };
  return messages[status] || null;
};

const RETRYABLE_SERVICE_COUNT_STATUSES = new Set([
  'INVALID_EVENT_LINK',
  'INVALID_SERVICE_METADATA',
  'PROCESSING_FAILED',
  'SERVICE_NOT_FOUND',
  'UNMATCHED',
]);

type ServiceCountRetry = {
  message: string;
  path: string;
};

const getServiceCountRetry = (serviceCountStatus: unknown, retryPath: unknown): ServiceCountRetry | null => {
  const status = typeof serviceCountStatus === 'string' ? serviceCountStatus : '';
  const path = typeof retryPath === 'string' ? retryPath.trim() : '';
  const message = getServiceCountWarning(status);
  if (!RETRYABLE_SERVICE_COUNT_STATUSES.has(status) || !path.startsWith('/o/') || !message) {
    return null;
  }
  return { message, path };
};

const getRequirementActionError = (detail: unknown, fallback: string): string => {
  if (typeof detail === 'string') {
    const messages: Record<string, string> = {
      AMBIGUOUS_REQUIREMENT_TRIGGER: 'Multiple compliance triggers are configured for this requirement.',
      ERROR: fallback,
      Forbidden: 'You do not have permission to manage requirements.',
      GUEST_NOT_FOUND: 'The attendee is no longer assigned to this event.',
      NOT_FOUND: 'The event was not found.',
      'Multiple requirement triggers found': 'Multiple compliance triggers are configured for this requirement.',
      'Requirement trigger not found': 'No compliance trigger is configured for this requirement.',
    };

    return messages[detail] || detail;
  }

  if (Array.isArray(detail)) {
    const validationMessages = detail
      .map((item) => {
        if (!item || typeof item !== 'object' || !('msg' in item)) {
          return null;
        }
        return typeof item.msg === 'string' ? item.msg : null;
      })
      .filter((message): message is string => Boolean(message));

    if (validationMessages.length) {
      return validationMessages.join(' ');
    }
  }

  return fallback;
};

const getResponseError = (response: any, fallback: string): string => {
  if (!response) return fallback;
  if (response.status && response.status >= 500) {
    return 'A server error occurred. Please try again later.';
  }
  return getRequirementActionError(response?.body?.detail, fallback);
};

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
  event_id: string;
};

interface AccountEventBannerProps {
  event: AccountEvent;
  sx?: SxProps;
}

export function AccountEventBanner({ event, sx }: AccountEventBannerProps): JSX.Element {
  const { theme } = useThemeContext();
  const title = event.title?.trim() || 'Untitled Event';
  const dateStr = formatEventDateTime(event.event_start_date, event.event_end_date);

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
        padding: '12px 16px',
        ...sx,
      }}
    >
      <Typography
        variant="h2"
        sx={{
          color: theme.custom.components.headerBanner.title.color,
          fontWeight: 400,
          fontSize: '28px',
          lineHeight: '32px',
          marginBottom: '8px',
          letterSpacing: '-0.25px',
        }}
      >
        {title}
      </Typography>
      {dateStr && dateStr !== '-' ? (
        <Typography
          variant="body1"
          sx={{ color: theme.custom.components.headerBanner.title.color, mb: 1, fontWeight: 500 }}
        >
          {dateStr}
        </Typography>
      ) : null}
    </Card>
  );
}

const SERVICES_METADATA_ERROR_MESSAGE = 'Unable to load or update services. Please try again.';
const EMPLOYEE_UPLOAD_ERROR_MESSAGE = 'Something went wrong while uploading the document. Please try again.';

interface AccountEventDetailsCardProps {
  event: AccountEvent;
  sx?: SxProps;
  onDeleteAssignedStaff?: (member: OrganizationStaffMember) => void;
  deletingStaffId?: number | null;
  canViewGuestDriveFolders?: boolean;
  canMoveEmployees?: boolean;
  onRequirementsUpdated?: () => Promise<void> | void;
}

interface EventDetailRowProps {
  label: string;
  value: ReactNode;
  trailing?: ReactNode;
  sx?: SxProps;
}

function EventDetailRow({ label, value, trailing, sx }: EventDetailRowProps): JSX.Element {
  return (
    <Box sx={{ mb: 1, ...sx }}>
      {label}:{' '}
      <Typography component="span" variant="body2" color="text.secondary">
        {value}
      </Typography>
      {trailing}
    </Box>
  );
}

const formatGuests = (guests: AccountEvent['guests']): AccountEventGuest[] => {
  if (!Array.isArray(guests)) {
    return [];
  }

  const seen = new Set<string>();
  const formatted: AccountEventGuest[] = [];

  guests.forEach((guest) => {
    if (!guest || typeof guest !== 'object') return;

    const g = guest as AccountEventGuest;

    const email = g.email;
    const userId = g.user_id;
    const patientId = g.patient_id;
    const fullName = (g.full_name ?? null) as string | null;
    const requirements = Array.isArray(g.requirements) ? g.requirements : undefined;

    if (!email && !userId) return;

    const key = (userId || email).toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);

    formatted.push({
      email: email,
      user_id: userId,
      patient_id: patientId,
      full_name: fullName,
      storage_folder_map: g.storage_folder_map,
      requirements: requirements,
      vitals: g.vitals || null,
    });
  });

  const getLastName = (guest: AccountEventGuest) => {
    const parts = (guest.full_name ?? '').trim().split(/\s+/);
    return parts.length > 1 ? parts.slice(1).join(' ') : parts[0] ?? '';
  };

  return formatted.sort((a, b) => getLastName(a).localeCompare(getLastName(b), undefined, { sensitivity: 'base' }));
};

const isRequirementCompleted = (requirement: EventRequirement): boolean => requirement.status === 'PASSED';

type RequirementStatusConfig = {
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  label: string;
};

const REQUIREMENT_STATUS_CONFIG: Record<EventRequirement['status'], RequirementStatusConfig> = {
  PENDING: { color: 'warning', label: 'Pending' },
  SUBMITTED: { color: 'info', label: 'Submitted' },
  IN_REVIEW: { color: 'primary', label: 'Under Review' },
  PASSED: { color: 'success', label: 'Passed' },
  FAILED: { color: 'error', label: 'Failed' },
  EXPIRED: { color: 'default', label: 'Expired' },
  OVERDUE: { color: 'error', label: 'Overdue' },
};

const getRequirementTypeLabel = (requirement: EventRequirement): string => {
  const name = requirement.attendee_req_name.toLowerCase();
  if (name.includes('fit test') || name.includes('respirator fit')) return 'Fit Test';
  if (name.includes('vaccin') || name.includes('immuniz')) return 'Vaccination';
  return requirement.req_type === 'form' ? 'Form' : 'Test';
};

const formatRequirementDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const getRequirementIcon = (requirement: EventRequirement, lungsIconSrc: string): JSX.Element => {
  const name = requirement.attendee_req_name.trim().toLowerCase();

  if (name.includes('respirator fit') || name.includes('fit test')) {
    return <MasksOutlinedIcon />;
  }
  if (name.includes('pulmonary')) {
    return (
      <Box
        component="span"
        sx={{
          backgroundImage: `url(${lungsIconSrc})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          display: 'inline-block',
          height: 32,
          width: 32,
        }}
      />
    );
  }

  return <AssignmentTurnedInOutlinedIcon />;
};

const areGuestRequirementsCompleted = (guest: AccountEventGuest): boolean => {
  const requirements = guest.requirements || [];
  return requirements.length > 0 && requirements.every(isRequirementCompleted);
};

function RequirementStatusIcon({ requirement }: { requirement: EventRequirement }): JSX.Element {
  const { theme } = useThemeContext();
  const completed = isRequirementCompleted(requirement);
  const statusLabel = REQUIREMENT_STATUS_CONFIG[requirement.status].label;
  const name = requirement.attendee_req_name;
  const lungsIconSrc = completed
    ? theme.images.requirementIcons.lungs.highlighted
    : theme.images.requirementIcons.lungs.default;

  return (
    <Tooltip title={`${name}: ${statusLabel}`}>
      <Box
        component="span"
        sx={{
          alignItems: 'center',
          color: completed ? 'success.dark' : 'text.secondary',
          display: 'inline-flex',
          height: 34,
          justifyContent: 'center',
          width: 34,
          '& .MuiSvgIcon-root': {
            fontSize: 32,
          },
        }}
      >
        {getRequirementIcon(requirement, lungsIconSrc)}
      </Box>
    </Tooltip>
  );
}

export function AccountEventDetailsCard({
  event,
  sx,
  onDeleteAssignedStaff,
  deletingStaffId = null,
  canViewGuestDriveFolders = false,
  canMoveEmployees = false,
  onRequirementsUpdated,
}: AccountEventDetailsCardProps): JSX.Element {
  const { theme } = useThemeContext();
  const { apiFetchRequest } = useBackendApi();
  const navigate = useNavigate();
  const { org_path_name, acc_path_name } = useParams<RouteParams>() as RouteParams;
  const [assignedStaff, setAssignedStaff] = useState<OrganizationStaffMember[]>([]);
  const [linkedForms, setLinkedForms] = useState<EventForm[]>([]);
  const [assignedStaffLoaded, setAssignedStaffLoaded] = useState(false);
  const [linkedFormsLoaded, setLinkedFormsLoaded] = useState(false);
  const [assignedStaffError, setAssignedStaffError] = useState(false);
  const [linkedFormsError, setLinkedFormsError] = useState(false);
  const [clearanceStatuses, setClearanceStatuses] = useState<EventEmployeeClearanceStatus[]>([]);
  const clearanceStatusRequestIdRef = useRef(0);
  const [isClearanceReportAvailable, setIsClearanceReportAvailable] = useState(false);
  const [isClearanceReportDownloading, setIsClearanceReportDownloading] = useState(false);
  const [requirementForms, setRequirementForms] = useState<EventRequirementFormOption[]>([]);
  const [requirementTests, setRequirementTests] = useState<EventRequirementTestOption[]>([]);
  const [requirementActionError, setRequirementActionError] = useState<string | null>(null);
  const [addingRequirement, setAddingRequirement] = useState<{
    userId: string;
    type: 'form' | 'physical_test';
  } | null>(null);
  const [uploadSelection, setUploadSelection] = useState<RequirementDialogSelection | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
  const [isPftReupload, setIsPftReupload] = useState(false);
  const [failureSelection, setFailureSelection] = useState<RequirementDialogSelection | null>(null);
  const [failureDialogOpen, setFailureDialogOpen] = useState(false);
  const [commentSelection, setCommentSelection] = useState<RequirementDialogSelection | null>(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [retakeSelection, setRetakeSelection] = useState<RequirementDialogSelection | null>(null);
  const [retakeDialogOpen, setRetakeDialogOpen] = useState(false);
  const [retakeReasonSelection, setRetakeReasonSelection] = useState<RetakeReasonSelection | null>(null);
  const [retakeReasonDialogOpen, setRetakeReasonDialogOpen] = useState(false);
  const [vitalsSelection, setVitalsSelection] = useState<AccountEventGuest | null>(null);
  const [vitalsDialogOpen, setVitalsDialogOpen] = useState(false);
  const [viewVitalsSelection, setViewVitalsSelection] = useState<AccountEventGuest | null>(null);
  const [viewVitalsDialogOpen, setViewVitalsDialogOpen] = useState(false);
  const [vitalsFormKey, setVitalsFormKey] = useState(0);
  const [isSavingVitals, setIsSavingVitals] = useState(false);
  const [failureComment, setFailureComment] = useState('');
  const [retakeReasonCode, setRetakeReasonCode] = useState('');
  const [retakeOtherComment, setRetakeOtherComment] = useState('');
  const [requirementMutationKey, setRequirementMutationKey] = useState<string | null>(null);
  const selectedRetakeReason = retakeSelection?.requirement.retake_reasons?.find(
    (reason) => reason.code === retakeReasonCode,
  );
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();

  // Employee upload modal states
  const [employeeUploadModalOpen, setEmployeeUploadModalOpen] = useState(false);
  const [employeeUploadFile, setEmployeeUploadFile] = useState<File | null>(null);
  const [employeeUploadStatus, setEmployeeUploadStatus] = useState<'uploading' | 'verifying' | 'success' | 'error'>(
    'uploading',
  );
  const [employeeUploadError, setEmployeeUploadError] = useState<string | null>(null);
  const [uploadTargetEmployeeName, setUploadTargetEmployeeName] = useState<string>('');
  const [selectedMoveUserIds, setSelectedMoveUserIds] = useState<Set<string>>(new Set());
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTargetEvents, setMoveTargetEvents] = useState<AccountEvent[]>([]);
  const [moveTargetEventId, setMoveTargetEventId] = useState<number | ''>('');
  const [moveTargetsLoading, setMoveTargetsLoading] = useState(false);
  const [moveSubmitting, setMoveSubmitting] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const resetMoveStateAfterExitRef = useRef(false);

  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const closeRemoveDialog = useCallback(() => {
    if (removeSubmitting) return;
    setRemoveDialogOpen(false);
    setRemoveError(null);
  }, [removeSubmitting]);

  const [editGuest, setEditGuest] = useState<AccountEventGuest | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const employeeImportFileInputRef = useRef<HTMLInputElement>(null);
  const [employeeImportDialogOpen, setEmployeeImportDialogOpen] = useState(false);
  const [employeeImportStatus, setEmployeeImportStatus] = useState<'extracting' | 'review' | 'confirming' | 'error'>(
    'extracting',
  );
  const [employeeImportPreview, setEmployeeImportPreview] = useState<EmployeeImportPreview | null>(null);
  const [employeeImportValues, setEmployeeImportValues] = useState<EmployeeImportValues>(EMPTY_EMPLOYEE_IMPORT_VALUES);
  const [employeeImportFieldErrors, setEmployeeImportFieldErrors] = useState<EmployeeImportFieldErrors>({});
  const [employeeImportError, setEmployeeImportError] = useState<string | null>(null);
  const [employeeImportErrorAction, setEmployeeImportErrorAction] = useState<EmployeeImportErrorAction>(null);
  const [employeeImportFile, setEmployeeImportFile] = useState<File | null>(null);
  const [employeeImportUseConfirmedIdentity, setEmployeeImportUseConfirmedIdentity] = useState(false);
  const [employeeImportAccountIdentityConflict, setEmployeeImportAccountIdentityConflict] = useState(false);

  const isMountedRef = useRef(true);
  const metadataRequestIdRef = useRef(0);
  const currentEventIdRef = useRef(event.acc_event_id);

  useEffect(() => {
    currentEventIdRef.current = event.acc_event_id;
  }, [event.acc_event_id]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      metadataRequestIdRef.current += 1;
    };
  }, []);

  const [servicesMetadata, setServicesMetadata] = useState<ServiceRequestsMetadata | null>(null);
  const [servicesMetadataLoading, setServicesMetadataLoading] = useState(true);
  const [servicesMetadataUpdating, setServicesMetadataUpdating] = useState(false);
  const [servicesMetadataError, setServicesMetadataError] = useState<string | null>(null);
  const [serviceCountRetries, setServiceCountRetries] = useState<ServiceCountRetry[]>([]);
  const [retryingServiceCountPath, setRetryingServiceCountPath] = useState<string | null>(null);
  const serviceCountsFinalized = event.event_completion_status === 'COMPLETED';

  const rememberServiceCountRetry = useCallback((serviceCountStatus: unknown, retryPath: unknown) => {
    const retry = getServiceCountRetry(serviceCountStatus, retryPath);
    if (!retry) return;
    setServiceCountRetries((current) => [...current.filter((item) => item.path !== retry.path), retry]);
  }, []);

  const handleQuantityChange = useCallback(
    async (index: number, change: number) => {
      if (!servicesMetadata || servicesMetadataUpdating || serviceCountsFinalized) return;

      const updatedServices = [...(servicesMetadata.services_requested || [])];
      if (!updatedServices[index]) return;

      const targetItem = { ...updatedServices[index] };
      const newCount = (targetItem.requested_count || 0) + change;
      if (newCount < 0) return;

      targetItem.requested_count = newCount;
      updatedServices[index] = targetItem;

      const newMetadata = {
        ...servicesMetadata,
        services_requested: updatedServices,
      };

      setServicesMetadataError(null);
      setServicesMetadataUpdating(true);
      try {
        const success = await updateAccountEventMetadata({
          orgPathName: org_path_name,
          accPathName: acc_path_name,
          eventId: event.acc_event_id,
          metaKey: 'service_requests',
          metaValue: newMetadata,
          apiFetchRequest,
          fail: () => setServicesMetadataError(SERVICES_METADATA_ERROR_MESSAGE),
          navigate,
        });

        if (success) {
          setServicesMetadata(newMetadata);
          setServicesMetadataError(null);
        } else {
          setServicesMetadataError(SERVICES_METADATA_ERROR_MESSAGE);
        }
      } catch (err) {
        console.error('Failed to update services metadata:', err);
        setServicesMetadataError(SERVICES_METADATA_ERROR_MESSAGE);
      } finally {
        setServicesMetadataUpdating(false);
      }
    },
    [
      servicesMetadata,
      servicesMetadataUpdating,
      org_path_name,
      acc_path_name,
      event.acc_event_id,
      apiFetchRequest,
      navigate,
      serviceCountsFinalized,
    ],
  );

  const loadServicesMetadata = useCallback(
    async (showLoading = true) => {
      const requestId = ++metadataRequestIdRef.current;
      const targetEventId = event.acc_event_id;

      if (showLoading && isMountedRef.current) {
        setServicesMetadataLoading(true);
      }
      if (isMountedRef.current) {
        setServicesMetadataError(null);
      }

      const isCurrentRequest = () =>
        isMountedRef.current &&
        requestId === metadataRequestIdRef.current &&
        targetEventId === currentEventIdRef.current;

      try {
        const res = await fetchAccountEventMetadata({
          orgPathName: org_path_name,
          accPathName: acc_path_name,
          eventId: targetEventId,
          metaKey: 'service_requests',
          apiFetchRequest,
          fail: () => {
            if (isCurrentRequest()) {
              setServicesMetadataError(SERVICES_METADATA_ERROR_MESSAGE);
            }
          },
          navigate,
        });
        if (!isCurrentRequest()) return;
        if (res && res.meta_value) {
          setServicesMetadata(res.meta_value);
          setServicesMetadataError(null);
        } else if (!res) {
          setServicesMetadataError(SERVICES_METADATA_ERROR_MESSAGE);
        }
      } catch (err) {
        console.error('Failed to load services metadata:', err);
        if (isCurrentRequest()) {
          setServicesMetadataError(SERVICES_METADATA_ERROR_MESSAGE);
        }
      } finally {
        if (showLoading && isCurrentRequest()) {
          setServicesMetadataLoading(false);
        }
      }
    },
    [acc_path_name, apiFetchRequest, event.acc_event_id, navigate, org_path_name],
  );

  useEffect(() => {
    setServicesMetadata(null);
    setServicesMetadataLoading(true);
    setServicesMetadataError(null);
    void loadServicesMetadata(true);
    return () => {
      metadataRequestIdRef.current += 1;
    };
  }, [loadServicesMetadata]);

  const handleServiceCountRetry = useCallback(
    async (retry: ServiceCountRetry) => {
      if (retryingServiceCountPath) return;
      setRetryingServiceCountPath(retry.path);
      try {
        const response = await apiFetchRequest({
          endpoint: `${backendURL}${retry.path}`,
          method: 'POST',
        });
        if (!response || response.isError || response.status !== 200) {
          addAlertMessages({
            message: 'Unable to retry the saved result. Please try again.',
            severityType: 'error',
          });
          return;
        }

        const status = typeof response.body?.service_count_status === 'string' ? response.body.service_count_status : '';
        const warning = getServiceCountWarning(status);
        if (RETRYABLE_SERVICE_COUNT_STATUSES.has(status) && warning) {
          setServiceCountRetries((current) =>
            current.map((item) => (item.path === retry.path ? { ...item, message: warning } : item)),
          );
          addAlertMessages({ message: warning, severityType: 'warning' });
        } else {
          setServiceCountRetries((current) => current.filter((item) => item.path !== retry.path));
          const message =
            status === 'APPLIED'
              ? 'The saved result was processed and the service count was updated.'
              : status === 'ALREADY_APPLIED'
                ? 'The saved result was already included in the service count.'
                : warning || 'The saved result does not represent a countable service.';
          addAlertMessages({
            message,
            severityType: status === 'APPLIED' || status === 'ALREADY_APPLIED' ? 'success' : 'warning',
          });
        }
        await Promise.all([loadServicesMetadata(false), onRequirementsUpdated?.()]);
      } catch (error) {
        console.error('Failed to retry service count processing', error);
        addAlertMessages({
          message: 'Unable to retry the saved result. Please try again.',
          severityType: 'error',
        });
      } finally {
        setRetryingServiceCountPath(null);
      }
    },
    [addAlertMessages, apiFetchRequest, loadServicesMetadata, onRequirementsUpdated, retryingServiceCountPath],
  );

  const clearanceStatusByPatientId = useMemo(() => {
    return new Map(clearanceStatuses.map((employee) => [employee.patient_id, employee]));
  }, [clearanceStatuses]);
  const guestList = useMemo(
    () =>
      formatGuests(event.guests).map((guest) => ({
        ...guest,
        clearance_status:
          clearanceStatusByPatientId.get(guest.patient_id || guest.user_id || '')?.clearance_status || null,
      })),
    [clearanceStatusByPatientId, event.guests],
  );
  const canManageRequirements =
    event.viewer_org_roles?.some((role) => ['admin', 'member'].includes(role.toLowerCase())) || false;
  const canViewClearanceStatuses =
    canManageRequirements || event.viewer_account_roles?.some((role) => role.toLowerCase() === 'admin') || false;
  const canImportEmployees = event.viewer_org_roles?.some((role) => role.toLowerCase() === 'admin') || false;
  const locationLabel = event.location_name;
  const locationMapUrl = event.location_map_url;
  const locationIcon = theme.images.locationIcon.default;

  const refreshClearanceStatuses = useCallback(async () => {
    if (!canViewClearanceStatuses) {
      clearanceStatusRequestIdRef.current += 1;
      setClearanceStatuses([]);
      return;
    }

    const requestId = ++clearanceStatusRequestIdRef.current;
    const response = await fetchEventClearanceStatuses({
      accPathName: acc_path_name,
      apiFetchRequest,
      eventId: event.acc_event_id,
      fail: () => undefined,
      navigate,
      orgPathName: org_path_name,
    });
    if (response && requestId === clearanceStatusRequestIdRef.current) {
      setClearanceStatuses(response.employees);
    }
  }, [acc_path_name, apiFetchRequest, canViewClearanceStatuses, event.acc_event_id, navigate, org_path_name]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pickingGuestIdRef = useRef<string | null>(null);
  const [uploadingGuestId, setUploadingGuestId] = useState<string | null>(null);

  useEffect(() => {
    resetMoveStateAfterExitRef.current = false;
    setSelectedMoveUserIds(new Set());
    setMoveDialogOpen(false);
    setMoveTargetEventId('');
    setMoveError(null);
  }, [event.acc_event_id]);

  const toggleMoveSelection = useCallback((guest: AccountEventGuest) => {
    const userId = guest.user_id;
    if (!userId) return;
    setSelectedMoveUserIds((previous) => {
      const next = new Set(previous);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const openMoveDialog = useCallback(async () => {
    if (!canMoveEmployees || !selectedMoveUserIds.size) return;

    setMoveDialogOpen(true);
    setMoveTargetsLoading(true);
    setMoveTargetEventId('');
    setMoveError(null);
    try {
      const response = await apiFetchRequest({
        method: 'GET',
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
          acc_path_name,
        )}/events/?start_after=${encodeURIComponent('1970-01-01T00:00:00')}`,
      });
      if (!response || response.isError || response.status !== 200) {
        setMoveError('Unable to load destination events.');
        return;
      }

      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      const events = Array.isArray(response.body?.events) ? (response.body.events as AccountEvent[]) : [];
      setMoveTargetEvents(
        events.filter((candidate) => {
          if (candidate.acc_event_id === event.acc_event_id) return false;
          if (!candidate.event_end_date) return true;
          const end = new Date(candidate.event_end_date);
          const endTime = end.getTime();
          if (Number.isNaN(endTime)) return false;
          end.setHours(0, 0, 0, 0);
          return end.getTime() >= currentDate.getTime();
        }),
      );
    } catch {
      setMoveError('Unable to load destination events.');
    } finally {
      setMoveTargetsLoading(false);
    }
  }, [acc_path_name, apiFetchRequest, canMoveEmployees, event.acc_event_id, org_path_name, selectedMoveUserIds.size]);

  const handleMoveEmployees = useCallback(async () => {
    if (!canMoveEmployees || !moveTargetEventId || !selectedMoveUserIds.size) return;

    setMoveSubmitting(true);
    setMoveError(null);
    try {
      const response = await apiFetchRequest({
        method: 'POST',
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
          acc_path_name,
        )}/events/${encodeURIComponent(String(event.acc_event_id))}/attendees/move`,
        body: {
          target_event_id: moveTargetEventId,
          user_ids: Array.from(selectedMoveUserIds),
        },
      });
      if (!response || response.isError || response.status !== 200) {
        const detail = typeof response?.body === 'string' ? response.body : response?.body?.detail;
        setMoveError(detail || 'Unable to move the selected employees.');
        return;
      }

      const warnings = Array.isArray(response.body?.integration_warnings) ? response.body.integration_warnings : [];
      addAlertMessages({
        message: warnings.length
          ? 'Employees moved. Calendar synchronization needs attention.'
          : 'Employees moved successfully.',
        severityType: warnings.length ? 'warning' : 'success',
      });
      resetMoveStateAfterExitRef.current = true;
      setMoveDialogOpen(false);
      await onRequirementsUpdated?.();
    } catch {
      setMoveError('Unable to move the selected employees.');
    } finally {
      setMoveSubmitting(false);
    }
  }, [
    acc_path_name,
    addAlertMessages,
    apiFetchRequest,
    canMoveEmployees,
    event.acc_event_id,
    moveTargetEventId,
    onRequirementsUpdated,
    org_path_name,
    selectedMoveUserIds,
  ]);

  const handleOpenEditDrawer = useCallback((guest: AccountEventGuest) => {
    const nameParts = (guest.full_name || '').trim().split(/\s+/).filter(Boolean);
    setEditGuest(guest);
    setEditFirstName(nameParts[0] || '');
    setEditLastName(nameParts.slice(1).join(' '));
    setEditEmail(guest.email || '');
    setEditError(null);
  }, []);

  const handleCloseEditDrawer = useCallback(() => {
    if (isSavingEdit) return;
    setEditGuest(null);
    setEditFirstName('');
    setEditLastName('');
    setEditEmail('');
    setEditError(null);
  }, [isSavingEdit]);

  const handleSaveEdit = useCallback(async () => {
    const guestId = editGuest?.user_id || editGuest?.patient_id;
    if (!guestId) return;

    const firstName = editFirstName.trim().replace(/\s+/g, ' ');
    const lastName = editLastName.trim().replace(/\s+/g, ' ');
    const email = editEmail.trim().toLowerCase();

    if (!firstName) {
      setEditError('First name is required.');
      return;
    }

    if (!email) {
      setEditError('Email is required.');
      return;
    }

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(email)) {
      setEditError('Please enter a valid email address.');
      return;
    }

    const originalNameParts = (editGuest?.full_name || '').trim().split(/\s+/).filter(Boolean);
    const originalFirstName = originalNameParts[0] || '';
    const originalLastName = originalNameParts.slice(1).join(' ');
    const originalEmail = editGuest?.email || '';

    const nameChanged = firstName !== originalFirstName || lastName !== originalLastName;
    const emailChanged = email !== originalEmail.toLowerCase();

    if (!nameChanged && !emailChanged) {
      setEditGuest(null);
      return;
    }

    setIsSavingEdit(true);
    setEditError(null);

    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
          acc_path_name,
        )}/events/${encodeURIComponent(String(event.acc_event_id))}/guests/${encodeURIComponent(guestId)}`,
        method: 'PATCH',
        body: { first_name: firstName, last_name: lastName, email },
      });

      if (!response || response.isError || response.status !== 200) {
        if (response?.status === 403) {
          setEditError('You do not have permission to edit attendee details.');
        } else if (response?.status === 404) {
          setEditError('This attendee is no longer assigned to the event.');
        } else if (response?.status === 409) {
          setEditError(response?.body?.detail || 'This email address is already in use.');
        } else {
          setEditError(getResponseError(response, 'Unable to update the attendee details.'));
        }
        setIsSavingEdit(false);
        return;
      }

      await onRequirementsUpdated?.();
      setEditGuest(null);

      let successMessage = 'Details updated successfully.';
      if (nameChanged && emailChanged) {
        successMessage = 'Name and email updated.';
      } else if (nameChanged) {
        successMessage = 'Name updated.';
      } else if (emailChanged) {
        successMessage = 'Email updated.';
      }

      addAlertMessages({ message: successMessage, severityType: 'success' });
    } catch (error) {
      console.error('Failed to update attendee details', error);
      setEditError('Unable to update the attendee details. Check your connection and try again.');
    } finally {
      setIsSavingEdit(false);
    }
  }, [
    acc_path_name,
    addAlertMessages,
    apiFetchRequest,
    event.acc_event_id,
    editFirstName,
    editLastName,
    editEmail,
    editGuest,
    onRequirementsUpdated,
    org_path_name,
  ]);

  const handleRemoveEmployees = useCallback(async () => {
    if (!canMoveEmployees || !selectedMoveUserIds.size) return;

    setRemoveSubmitting(true);
    setRemoveError(null);
    try {
      const response = await apiFetchRequest({
        method: 'POST',
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
          acc_path_name,
        )}/events/${encodeURIComponent(String(event.acc_event_id))}/guests/remove`,
        body: {
          user_ids: Array.from(selectedMoveUserIds),
        },
      });
      if (!response || response.isError || response.status !== 200) {
        const detail = typeof response?.body === 'string' ? response.body : response?.body?.detail;
        setRemoveError(detail || 'Unable to remove the selected employees.');
        return;
      }

      const warnings = Array.isArray(response.body?.integration_warnings) ? response.body.integration_warnings : [];
      addAlertMessages({
        message: warnings.length
          ? 'Employees removed. Calendar synchronization needs attention.'
          : 'Employees removed successfully.',
        severityType: warnings.length ? 'warning' : 'success',
      });
      setSelectedMoveUserIds(new Set());
      setRemoveDialogOpen(false);
      await onRequirementsUpdated?.();
    } catch {
      setRemoveError('Unable to remove the selected employees.');
    } finally {
      setRemoveSubmitting(false);
    }
  }, [
    acc_path_name,
    addAlertMessages,
    apiFetchRequest,
    canMoveEmployees,
    event.acc_event_id,
    onRequirementsUpdated,
    org_path_name,
    selectedMoveUserIds,
  ]);

  const handleUploadClick = useCallback((row: AccountEventGuest) => {
    const guestId = row.user_id || row.patient_id;
    if (!guestId) return;
    pickingGuestIdRef.current = guestId;
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 50);
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      const targetGuestId = pickingGuestIdRef.current;
      if (!files || files.length === 0 || !targetGuestId) {
        pickingGuestIdRef.current = null;
        return;
      }

      const file = files[0];
      setEmployeeUploadFile(file);
      setEmployeeUploadStatus('uploading');
      setEmployeeUploadError(null);

      // Find employee name for display in the modal
      const targetGuest = guestList.find((g) => g.user_id === targetGuestId || g.patient_id === targetGuestId);
      setUploadTargetEmployeeName(targetGuest?.full_name || targetGuest?.email || 'Employee');

      setEmployeeUploadModalOpen(true);
      setUploadingGuestId(targetGuestId);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await apiFetchRequest({
          method: 'POST',
          endpoint: `${backendURL}/o/${org_path_name}/a/${acc_path_name}/events/${event.acc_event_id}/guests/${targetGuestId}/upload`,
          body: formData,
        });

        if (res && !res.isError && res.status === 201) {
          setEmployeeUploadStatus('success');
          const countWarning = getServiceCountWarning(res.body?.service_count_status);
          rememberServiceCountRetry(res.body?.service_count_status, res.body?.retry_path);
          addAlertMessages({
            message: countWarning || `Successfully uploaded "${file.name}" to Google Drive folder!`,
            severityType: countWarning ? 'warning' : 'success',
          });
          // Refresh list and services metadata concurrently!
          await Promise.all([loadServicesMetadata(false), onRequirementsUpdated?.()]);

          // Auto close modal after 1.5 seconds on success
          setTimeout(() => {
            setEmployeeUploadModalOpen(false);
          }, 1500);
        } else {
          const errorMsg = res?.body?.detail || `Failed to upload and verify "${file.name}".`;
          setEmployeeUploadStatus('error');
          setEmployeeUploadError(errorMsg);
          addAlertMessages({ message: errorMsg, severityType: 'error' });
        }
      } catch (err) {
        console.error('File upload error:', err);
        const errorMsg = err instanceof Error ? err.message : String(err);
        setEmployeeUploadStatus('error');
        setEmployeeUploadError(`Error uploading file: ${errorMsg}`);
        addAlertMessages({
          message: `Error uploading file: ${errorMsg}`,
          severityType: 'error',
        });
      } finally {
        setUploadingGuestId(null);
        pickingGuestIdRef.current = null;
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [
      org_path_name,
      acc_path_name,
      event.acc_event_id,
      apiFetchRequest,
      addAlertMessages,
      guestList,
      loadServicesMetadata,
      onRequirementsUpdated,
      rememberServiceCountRetry,
    ],
  );

  const resetEmployeeImport = useCallback(() => {
    setEmployeeImportDialogOpen(false);
    setEmployeeImportStatus('extracting');
    setEmployeeImportPreview(null);
    setEmployeeImportValues(EMPTY_EMPLOYEE_IMPORT_VALUES);
    setEmployeeImportFieldErrors({});
    setEmployeeImportError(null);
    setEmployeeImportErrorAction(null);
    setEmployeeImportFile(null);
    setEmployeeImportUseConfirmedIdentity(false);
    setEmployeeImportAccountIdentityConflict(false);
  }, []);

  const handleEmployeeImportPreview = useCallback(
    async (selectedFile: File) => {
      setEmployeeImportDialogOpen(true);
      setEmployeeImportPreview(null);
      setEmployeeImportValues(EMPTY_EMPLOYEE_IMPORT_VALUES);
      setEmployeeImportFieldErrors({});
      setEmployeeImportError(null);
      setEmployeeImportErrorAction(null);
      setEmployeeImportUseConfirmedIdentity(false);
      setEmployeeImportAccountIdentityConflict(false);

      const hasPdfType = selectedFile.type === 'application/pdf';
      const hasPdfExtension = selectedFile.name.toLowerCase().endsWith('.pdf');
      if (!hasPdfType && !hasPdfExtension) {
        setEmployeeImportFile(null);
        setEmployeeImportError('Choose a PDF version of the Respiratory Protection Medical Form.');
        setEmployeeImportErrorAction('choose-file');
        setEmployeeImportStatus('error');
        return;
      }
      if (selectedFile.size === 0) {
        setEmployeeImportFile(null);
        setEmployeeImportError('This PDF is empty. Choose a non-empty form.');
        setEmployeeImportErrorAction('choose-file');
        setEmployeeImportStatus('error');
        return;
      }
      if (selectedFile.size > MAX_EMPLOYEE_IMPORT_FILE_BYTES) {
        setEmployeeImportFile(null);
        setEmployeeImportError('This PDF is larger than 10 MB. Choose a smaller PDF.');
        setEmployeeImportErrorAction('choose-file');
        setEmployeeImportStatus('error');
        return;
      }

      const file = hasPdfType
        ? selectedFile
        : new File([selectedFile], selectedFile.name, {
            lastModified: selectedFile.lastModified,
            type: 'application/pdf',
          });
      setEmployeeImportFile(file);
      setEmployeeImportStatus('extracting');

      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await apiFetchRequest({
          method: 'POST',
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
            acc_path_name,
          )}/events/${encodeURIComponent(String(event.acc_event_id))}/employee-import/preview`,
          body: formData,
        });
        if (!response || response.isError || response.status !== 200) {
          const detail = typeof response?.body === 'string' ? response.body : response?.body?.detail;
          const resolvedError = getEmployeeImportError(response?.status, detail, 'preview');
          setEmployeeImportError(resolvedError.message);
          setEmployeeImportErrorAction(resolvedError.action);
          setEmployeeImportStatus('error');
          return;
        }

        const preview = response.body as EmployeeImportPreview | null;
        if (!preview?.preview_token || !preview.employee || !preview.classification) {
          setEmployeeImportError(
            'The extraction service returned an incomplete response. Try extracting the form again.',
          );
          setEmployeeImportErrorAction('retry-preview');
          setEmployeeImportStatus('error');
          return;
        }

        setEmployeeImportPreview(preview);
        setEmployeeImportValues({
          firstName: preview.employee.first_name || '',
          lastName: preview.employee.last_name || '',
          birthdate: preview.employee.birthdate || '',
          phoneNumber: preview.employee.phone_number || '',
          gender: '',
          email: preview.employee.email || '',
        });
        setEmployeeImportStatus('review');
      } catch (error) {
        console.error('Employee form extraction failed', error);
        const resolvedError = getEmployeeImportError(undefined, null, 'preview');
        setEmployeeImportError(resolvedError.message);
        setEmployeeImportErrorAction(resolvedError.action);
        setEmployeeImportStatus('error');
      }
    },
    [acc_path_name, apiFetchRequest, event.acc_event_id, org_path_name],
  );

  const handleEmployeeImportFile = useCallback(
    (inputEvent: React.ChangeEvent<HTMLInputElement>) => {
      const file = inputEvent.target.files?.[0];
      inputEvent.target.value = '';
      if (file) void handleEmployeeImportPreview(file);
    },
    [handleEmployeeImportPreview],
  );

  const handleEmployeeImportConfirm = useCallback(async () => {
    if (!employeeImportPreview || !employeeImportFile) return;
    const fieldErrors = validateEmployeeImportValues(employeeImportValues);
    setEmployeeImportFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      setEmployeeImportError('Correct the highlighted employee details before creating the account.');
      setEmployeeImportErrorAction(null);
      return;
    }

    setEmployeeImportStatus('confirming');
    setEmployeeImportError(null);
    setEmployeeImportErrorAction(null);
    try {
      const formData = new FormData();
      formData.append('preview_token', employeeImportPreview.preview_token);
      formData.append('first_name', employeeImportValues.firstName.trim());
      formData.append('last_name', employeeImportValues.lastName.trim());
      formData.append('birthdate', employeeImportValues.birthdate);
      formData.append('phone_number', employeeImportValues.phoneNumber.trim());
      formData.append('gender', employeeImportValues.gender);
      formData.append('email', employeeImportValues.email.trim());
      formData.append('use_confirmed_identity', String(employeeImportUseConfirmedIdentity));
      formData.append('file', employeeImportFile);

      const response = await apiFetchRequest({
        method: 'POST',
        endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
          acc_path_name,
        )}/events/${encodeURIComponent(String(event.acc_event_id))}/employee-import/confirm`,
        body: formData,
      });
      if (!response || response.isError || response.status !== 201) {
        const detail = typeof response?.body === 'string' ? response.body : response?.body?.detail;
        const hasAccountIdentityConflict = isEmployeeImportAccountIdentityConflict(response?.status, detail);
        const resolvedError = getEmployeeImportError(response?.status, detail, 'confirm');
        setEmployeeImportError(resolvedError.message);
        setEmployeeImportErrorAction(resolvedError.action);
        setEmployeeImportAccountIdentityConflict(hasAccountIdentityConflict);
        if (!hasAccountIdentityConflict) {
          setEmployeeImportUseConfirmedIdentity(false);
        }
        setEmployeeImportStatus(resolvedError.action === 'reprocess' ? 'error' : 'review');
        return;
      }

      const uploadStatus = response.body?.upload_status;
      if (uploadStatus && uploadStatus !== 'SUCCESS') {
        const full_name = response.body?.full_name || 'Employee';
        addAlertMessages({
          message: `${full_name} was created, but the requirement file could not be attached because ${getEmployeeImportUploadWarning(uploadStatus)}.`,
          severityType: 'warning',
        });
      } else {
        const countWarning = getServiceCountWarning(response.body?.service_count_status);
        rememberServiceCountRetry(
          response.body?.service_count_status,
          response.body?.service_count_retry_path,
        );
        addAlertMessages({
          message:
            countWarning || `${response.body?.full_name || 'Employee'} was added to the account and event.`,
          severityType: countWarning ? 'warning' : 'success',
        });
      }

      resetEmployeeImport();
      try {
        await Promise.all([loadServicesMetadata(false), onRequirementsUpdated?.()]);
      } catch (refreshError) {
        console.error('Employee was added but the event refresh failed', refreshError);
        addAlertMessages({
          message: 'The employee was added, but the employee list could not be refreshed. Refresh the page to see it.',
          severityType: 'warning',
        });
      }
    } catch (error) {
      console.error('Employee creation request failed', error);
      const resolvedError = getEmployeeImportError(undefined, null, 'confirm');
      setEmployeeImportError(resolvedError.message);
      setEmployeeImportErrorAction(resolvedError.action);
      setEmployeeImportStatus('review');
    }
  }, [
    acc_path_name,
    addAlertMessages,
    apiFetchRequest,
    employeeImportPreview,
    employeeImportFile,
    employeeImportUseConfirmedIdentity,
    employeeImportValues,
    event.acc_event_id,
    loadServicesMetadata,
    onRequirementsUpdated,
    org_path_name,
    rememberServiceCountRetry,
    resetEmployeeImport,
  ]);

  useEffect(() => {
    let cancelled = false;

    setAssignedStaffLoaded(false);
    setAssignedStaffError(false);

    void fetchAccountEventAssignedStaff({
      accPathName: acc_path_name,
      apiFetchRequest,
      eventId: event.acc_event_id,
      fail: () => {
        if (cancelled) {
          return;
        }
        setAssignedStaff([]);
        setAssignedStaffError(true);
        setAssignedStaffLoaded(true);
      },
      navigate,
      orgPathName: org_path_name,
    }).then((staff) => {
      if (cancelled || !staff) {
        return;
      }
      setAssignedStaff(staff);
      setAssignedStaffLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [acc_path_name, apiFetchRequest, event, navigate, org_path_name]);

  useEffect(() => {
    let cancelled = false;

    setLinkedFormsLoaded(false);
    setLinkedFormsError(false);

    void fetchAccountEventForms({
      accPathName: acc_path_name,
      apiFetchRequest,
      eventId: event.acc_event_id,
      fail: () => {
        if (cancelled) {
          return;
        }
        setLinkedForms([]);
        setLinkedFormsError(true);
        setLinkedFormsLoaded(true);
      },
      navigate,
      orgPathName: org_path_name,
    }).then((forms) => {
      if (cancelled || !forms) {
        return;
      }
      setLinkedForms(forms);
      setLinkedFormsLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [acc_path_name, apiFetchRequest, event, navigate, org_path_name]);

  useEffect(() => {
    if (!canViewClearanceStatuses) {
      clearanceStatusRequestIdRef.current += 1;
      setClearanceStatuses([]);
      setIsClearanceReportAvailable(false);
      return;
    }

    void refreshClearanceStatuses();

    return () => {
      clearanceStatusRequestIdRef.current += 1;
    };
  }, [canViewClearanceStatuses, refreshClearanceStatuses]);

  useEffect(() => {
    const hasProcessingClearance = clearanceStatuses.some((item) => item.clearance_status === 'PROCESSING');
    if (!canViewClearanceStatuses || !hasProcessingClearance) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId: number | undefined;
    const poll = async () => {
      await refreshClearanceStatuses();
      if (!cancelled) {
        timeoutId = window.setTimeout(() => void poll(), 5_000);
      }
    };

    timeoutId = window.setTimeout(() => void poll(), 5_000);
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [canViewClearanceStatuses, clearanceStatuses, refreshClearanceStatuses]);

  useEffect(() => {
    if (!canViewClearanceStatuses) {
      setIsClearanceReportAvailable(false);
      return;
    }

    let cancelled = false;
    void checkEventClearanceReportAvailability({
      accPathName: acc_path_name,
      apiFetchRequest,
      eventId: event.acc_event_id,
      fail: () => {
        if (!cancelled) {
          setIsClearanceReportAvailable(false);
        }
      },
      navigate,
      orgPathName: org_path_name,
    }).then((available) => {
      if (!cancelled && available !== null) {
        setIsClearanceReportAvailable(available);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [acc_path_name, apiFetchRequest, canViewClearanceStatuses, event.acc_event_id, navigate, org_path_name]);

  const handleClearanceReportDownload = useCallback(async () => {
    setIsClearanceReportDownloading(true);
    try {
      const report = await downloadEventClearanceReport({
        accPathName: acc_path_name,
        apiFetchRequest,
        eventId: event.acc_event_id,
        fail: () => {
          addAlertMessages({
            message: 'Unable to download the clearance report.',
            severityType: 'error',
          });
        },
        navigate,
        orgPathName: org_path_name,
      });
      if (!report) {
        setIsClearanceReportAvailable(false);
        return;
      }

      const objectUrl = URL.createObjectURL(report);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `event-${event.acc_event_id}-guest-report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } finally {
      setIsClearanceReportDownloading(false);
    }
  }, [acc_path_name, addAlertMessages, apiFetchRequest, event.acc_event_id, navigate, org_path_name]);

  useEffect(() => {
    if (!canManageRequirements) {
      setRequirementForms([]);
      setRequirementTests([]);
      return;
    }

    let cancelled = false;

    void Promise.all([
      fetchEventRequirementForms({
        accPathName: acc_path_name,
        apiFetchRequest,
        fail: () => {
          if (!cancelled) {
            setRequirementForms([]);
            setRequirementActionError('Unable to load forms that can be assigned.');
          }
        },
        navigate,
        orgPathName: org_path_name,
      }),
      fetchEventRequirementTests({
        accPathName: acc_path_name,
        apiFetchRequest,
        fail: () => {
          if (!cancelled) {
            setRequirementTests([]);
            setRequirementActionError('Unable to load tests that can be assigned.');
          }
        },
        navigate,
        orgPathName: org_path_name,
      }),
    ]).then(([forms, tests]) => {
      if (cancelled) return;
      if (forms) setRequirementForms(forms);
      if (tests) setRequirementTests(tests);
    });

    return () => {
      cancelled = true;
    };
  }, [acc_path_name, apiFetchRequest, canManageRequirements, navigate, org_path_name]);

  const handleAddRequirement = useCallback(
    async (guest: AccountEventGuest, type: 'form' | 'physical_test', id: number, name: string) => {
      const guestId = guest.user_id || guest.patient_id;
      if (!org_path_name || !acc_path_name || !event.acc_event_id || !guestId) {
        setRequirementActionError('Unable to identify the event or attendee for this requirement.');
        return;
      }

      setRequirementActionError(null);
      setAddingRequirement({ userId: guestId, type });

      try {
        const payload = {
          req_type: type,
          forms_catalog_id: type === 'form' ? id : undefined,
          tests_catalog_id: type === 'physical_test' ? id : undefined,
        };

        const response = await apiFetchRequest({
          method: 'POST',
          endpoint: `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
            acc_path_name,
          )}/events/${encodeURIComponent(String(event.acc_event_id))}/guests/${encodeURIComponent(
            guestId,
          )}/requirements`,
          body: payload,
        });

        if (!response || response.isError || response.status !== 201) {
          const fallback =
            response?.status === 404 ? 'The requirement assignment endpoint was not found.' : `Unable to add ${name}.`;
          setRequirementActionError(getResponseError(response, fallback));
          return;
        }

        try {
          await Promise.all([onRequirementsUpdated?.(), refreshClearanceStatuses()]);
        } catch (refreshError) {
          console.error('Failed to refresh requirements or clearance statuses', refreshError);
          setRequirementActionError(`${name} was added, but the attendee details could not be refreshed.`);
        }
      } catch (err) {
        console.error('Failed to add requirement', err);
        setRequirementActionError(`Unable to add ${name}. Check your connection and try again.`);
      } finally {
        setAddingRequirement(null);
      }
    },
    [
      acc_path_name,
      apiFetchRequest,
      event.acc_event_id,
      onRequirementsUpdated,
      org_path_name,
      refreshClearanceStatuses,
    ],
  );

  const getRequirementEndpoint = useCallback(
    (selection: RequirementDialogSelection, suffix: string): string => {
      return `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
        acc_path_name,
      )}/events/${encodeURIComponent(String(event.acc_event_id))}/guests/${encodeURIComponent(
        selection.guest.user_id || selection.guest.patient_id || '',
      )}/requirements/${encodeURIComponent(selection.requirement.requirement_id)}/${suffix}`;
    },
    [acc_path_name, event.acc_event_id, org_path_name],
  );

  const getRequirementQrCodePagePath = useCallback(
    (selection: RequirementDialogSelection): string | null => {
      const guestId = [selection.guest.user_id, selection.guest.patient_id]
        .map((id) => id?.trim())
        .find((id): id is string => Boolean(id));
      const requirementId = selection.requirement.requirement_id?.trim();
      if (!org_path_name || !acc_path_name || !event.acc_event_id || !guestId || !requirementId) {
        return null;
      }

      return `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
        acc_path_name,
      )}/events/${encodeURIComponent(String(event.acc_event_id))}/guests/${encodeURIComponent(
        guestId,
      )}/requirements/${encodeURIComponent(requirementId)}/qr-code`;
    },
    [acc_path_name, event.acc_event_id, org_path_name],
  );

  const refreshRequirements = useCallback(async () => {
    try {
      await onRequirementsUpdated?.();
    } catch (refreshError) {
      console.error('Failed to refresh requirements', refreshError);
      setRequirementActionError('The update succeeded, but requirements could not be refreshed.');
    }
  }, [onRequirementsUpdated]);

  const handleEvidenceDownload = useCallback(
    async (selection: RequirementDialogSelection) => {
      const evidencePath = selection.requirement.submitted_form_pdf_url || selection.requirement.uploaded_result_url;
      if (!evidencePath) {
        setRequirementActionError('Requirement evidence is not available.');
        return;
      }

      const mutationKey = `${selection.requirement.requirement_id}:download`;
      setRequirementMutationKey(mutationKey);
      setRequirementActionError(null);
      try {
        const response = await apiFetchRequest({
          endpoint: evidencePath.startsWith('http') ? evidencePath : `${backendURL}${evidencePath}`,
          method: 'GET',
          isFile: true,
        });
        if (!response || response.isError || !(response.body instanceof Blob)) {
          setRequirementActionError('Unable to download requirement evidence.');
          return;
        }

        const objectUrl = URL.createObjectURL(response.body);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch (error) {
        console.error('Failed to download requirement evidence', error);
        setRequirementActionError('Unable to download requirement evidence.');
      } finally {
        setRequirementMutationKey(null);
      }
    },
    [apiFetchRequest],
  );

  const handleReviewAction = useCallback(
    async (
      selection: RequirementDialogSelection,
      action: 'REVIEW_STARTED' | 'PASSED' | 'FAILED' | 'RETAKE_REQUESTED',
      comment?: string,
      retake?: { reasonCode: string; otherComment?: string },
    ) => {
      const mutationKey = `${selection.requirement.requirement_id}:${action}`;
      setRequirementMutationKey(mutationKey);
      setRequirementActionError(null);
      try {
        const response = await apiFetchRequest({
          endpoint: getRequirementEndpoint(selection, 'review-actions'),
          method: 'POST',
          body: {
            action,
            comment: comment?.trim(),
            reason_code: retake?.reasonCode,
            other_comment: retake?.otherComment?.trim(),
          },
        });
        if (!response || response.isError || response.status !== 201) {
          setRequirementActionError(
            getResponseError(
              response,
              'This requirement is no longer in the expected review status. Refresh and try again.',
            ),
          );
          return;
        }

        if (action === 'FAILED') {
          setFailureDialogOpen(false);
        } else if (action === 'RETAKE_REQUESTED') {
          setRetakeDialogOpen(false);
        }
        await refreshRequirements();
      } catch (error) {
        console.error('Failed to update requirement review status', error);
        setRequirementActionError('Unable to update requirement review status.');
      } finally {
        setRequirementMutationKey(null);
      }
    },
    [apiFetchRequest, getRequirementEndpoint, refreshRequirements],
  );

  const handleRequirementUpload = useCallback(async () => {
    if (!uploadSelection || !selectedUploadFile) {
      setRequirementActionError('Choose a file before uploading.');
      return;
    }

    const formData = new FormData();
    if (!isPftReupload) {
      formData.append(
        'artifact_type',
        uploadSelection.requirement.req_type === 'form' ? 'SUPPORTING_DOCUMENT' : 'TEST_RESULT',
      );
    }
    formData.append('file', selectedUploadFile);

    const mutationKey = `${uploadSelection.requirement.requirement_id}:${
      isPftReupload ? 'pft-reupload' : 'upload'
    }`;
    setRequirementMutationKey(mutationKey);
    setRequirementActionError(null);
    try {
      const response = await apiFetchRequest({
        endpoint: getRequirementEndpoint(uploadSelection, isPftReupload ? 'artifacts/reupload' : 'artifacts'),
        method: 'POST',
        body: formData,
      });
      if (!response || response.isError || response.status !== 201) {
        setRequirementActionError(getResponseError(response, 'Unable to upload requirement file.'));
        return;
      }

      const countWarning = getServiceCountWarning(response.body?.service_count_status);
      rememberServiceCountRetry(response.body?.service_count_status, response.body?.retry_path);
      if (countWarning) {
        addAlertMessages({ message: countWarning, severityType: 'warning' });
      }
      setUploadDialogOpen(false);
      await Promise.all([
        loadServicesMetadata(false),
        refreshRequirements(),
        refreshClearanceStatuses(),
      ]);
    } catch (error) {
      console.error('Failed to upload requirement file', error);
      setRequirementActionError('Unable to upload requirement file.');
    } finally {
      setRequirementMutationKey(null);
    }
  }, [
    addAlertMessages,
    apiFetchRequest,
    getRequirementEndpoint,
    isPftReupload,
    loadServicesMetadata,
    refreshClearanceStatuses,
    refreshRequirements,
    rememberServiceCountRetry,
    selectedUploadFile,
    uploadSelection,
  ]);

  const handlePftAnalysisRetry = useCallback(
    async (selection: RequirementDialogSelection) => {
      const mutationKey = `${selection.requirement.requirement_id}:pft-retry`;
      setRequirementMutationKey(mutationKey);
      setRequirementActionError(null);
      try {
        const response = await apiFetchRequest({
          endpoint: getRequirementEndpoint(selection, 'clearance-analysis/retry'),
          method: 'POST',
        });
        if (!response || response.isError || response.status !== 200) {
          setRequirementActionError(getResponseError(response, 'Unable to retry PFT clearance analysis.'));
          return;
        }
        await refreshClearanceStatuses();
      } catch (error) {
        console.error('Failed to retry PFT clearance analysis', error);
        setRequirementActionError('Unable to retry PFT clearance analysis.');
      } finally {
        setRequirementMutationKey(null);
      }
    },
    [apiFetchRequest, getRequirementEndpoint, refreshClearanceStatuses],
  );

  const renderExpandedRequirements = useCallback(
    (guest: AccountEventGuest): ReactNode => {
      const requirements = guest.requirements || [];
      if (!requirements.length) {
        return (
          <Typography variant="body2" color="text.secondary">
            No requirements assigned.
          </Typography>
        );
      }

      return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {requirements.map((requirement) => {
            const selection = { guest, requirement };
            const clearance = clearanceStatusByPatientId.get(guest.patient_id || guest.user_id || '');
            const isPftRequirement =
              requirement.req_type === 'physical_test' &&
              requirement.billing_service_key === 'PULMONARY_FUNCTION_TEST';
            const qrCodePagePath = getRequirementQrCodePagePath(selection);
            const hasEvidence = Boolean(requirement.submitted_form_pdf_url || requirement.uploaded_result_url);
            const statusConfig = REQUIREMENT_STATUS_CONFIG[requirement.status];
            const evidenceLabel =
              requirement.req_type === 'physical_test'
                ? 'View Result'
                : requirement.status === 'SUBMITTED' || requirement.status === 'IN_REVIEW'
                  ? 'View Submission'
                  : 'View File';
            const isMutating = requirementMutationKey?.startsWith(requirement.requirement_id);
            const hasIncomingRetakeReason = Boolean(requirement.retake_source_reason);
            const hasOutgoingRetakeReason = Boolean(requirement.retake_replacement_reason);
            const hasBothRetakeReasons = hasIncomingRetakeReason && hasOutgoingRetakeReason;

            return (
              <Box
                key={requirement.requirement_id}
                sx={{
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 2,
                }}
              >
                <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {requirement.attendee_req_name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {requirement.is_replacement_attempt ? (
                        <Chip label="Replacement attempt" size="small" variant="outlined" />
                      ) : null}
                      {requirement.has_retake_requested ? (
                        <Chip label="Retake requested" size="small" variant="outlined" />
                      ) : null}
                    </Box>
                  </Box>
                  <Chip color={statusConfig.color} label={statusConfig.label} size="small" />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(3, minmax(140px, 1fr))',
                    },
                    mt: 1.5,
                  }}
                >
                  {[
                    ['Type', getRequirementTypeLabel(requirement)],
                    ['Submit by', formatRequirementDate(requirement.due_date)],
                    ['Last updated', formatRequirementDate(requirement.last_updated)],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="body2">{value}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    alignItems: 'center',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 1.5,
                    pt: 1,
                  }}
                >
                  {requirement.status === 'PENDING' && requirement.req_type === 'form' && requirement.form_fill_url ? (
                    <>
                      <Button
                        component="a"
                        href={requirement.form_fill_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="contained"
                      >
                        Fill Form
                      </Button>
                      {qrCodePagePath ? (
                        <Button
                          component="a"
                          disabled={Boolean(isMutating)}
                          href={qrCodePagePath}
                          rel="noopener noreferrer"
                          startIcon={<QrCode2Icon />}
                          size="small"
                          target="_blank"
                          variant="outlined"
                        >
                          QR Code
                        </Button>
                      ) : null}
                      <CopyToClipboard text={requirement.form_fill_url} title="Copy form link" />
                    </>
                  ) : null}
                  {requirement.status === 'PENDING' && canManageRequirements ? (
                    <Button
                      size="small"
                      variant={requirement.form_fill_url ? 'outlined' : 'contained'}
                      disabled={Boolean(isMutating)}
                      onClick={() => {
                        setRequirementActionError(null);
                        setSelectedUploadFile(null);
                        setIsPftReupload(false);
                        setUploadSelection(selection);
                        setUploadDialogOpen(true);
                      }}
                    >
                      Upload File
                    </Button>
                  ) : null}
                  {isPftRequirement && clearance?.can_retry_analysis && canManageRequirements ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={Boolean(isMutating)}
                      onClick={() => void handlePftAnalysisRetry(selection)}
                    >
                      Retry Analysis
                    </Button>
                  ) : null}
                  {isPftRequirement && clearance?.can_reupload_pft && canManageRequirements ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={Boolean(isMutating)}
                      onClick={() => {
                        setRequirementActionError(null);
                        setSelectedUploadFile(null);
                        setIsPftReupload(true);
                        setUploadSelection(selection);
                        setUploadDialogOpen(true);
                      }}
                    >
                      Reupload PFT PDF
                    </Button>
                  ) : null}
                  {requirement.status === 'SUBMITTED' &&
                  requirement.req_type === 'physical_test' &&
                  hasEvidence &&
                  canManageRequirements ? (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={Boolean(isMutating)}
                      onClick={() => void handleReviewAction(selection, 'REVIEW_STARTED')}
                    >
                      Start Review
                    </Button>
                  ) : null}
                  {requirement.status === 'IN_REVIEW' &&
                  requirement.req_type === 'physical_test' &&
                  canManageRequirements ? (
                    <>
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        disabled={Boolean(isMutating)}
                        onClick={() => void handleReviewAction(selection, 'PASSED')}
                      >
                        Pass
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={Boolean(isMutating)}
                        onClick={() => {
                          setRequirementActionError(null);
                          setFailureComment('');
                          setFailureSelection(selection);
                          setFailureDialogOpen(true);
                        }}
                      >
                        Fail
                      </Button>
                    </>
                  ) : null}
                  {hasEvidence ? (
                    <Button
                      size="small"
                      variant="text"
                      disabled={Boolean(isMutating)}
                      onClick={() => void handleEvidenceDownload(selection)}
                    >
                      {evidenceLabel}
                    </Button>
                  ) : null}
                  {requirement.status === 'FAILED' && requirement.latest_failure ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setCommentSelection(selection);
                        setCommentDialogOpen(true);
                      }}
                    >
                      View Failure Reason
                    </Button>
                  ) : null}
                  {requirement.can_request_retake && canManageRequirements ? (
                    <Button
                      size="small"
                      color="warning"
                      variant="outlined"
                      disabled={Boolean(isMutating)}
                      onClick={() => {
                        setRequirementActionError(null);
                        setRetakeReasonCode('');
                        setRetakeOtherComment('');
                        setRetakeSelection(selection);
                        setRetakeDialogOpen(true);
                      }}
                    >
                      Request Retake
                    </Button>
                  ) : null}
                  {requirement.retake_source_reason ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setRetakeReasonSelection({
                          ...selection,
                          reason: requirement.retake_source_reason || '',
                          relationshipLabel: 'Replacement created from a failed attempt',
                        });
                        setRetakeReasonDialogOpen(true);
                      }}
                    >
                      {hasBothRetakeReasons ? 'View Incoming Retake Reason' : 'View Retake Reason'}
                    </Button>
                  ) : null}
                  {requirement.retake_replacement_reason ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        setRetakeReasonSelection({
                          ...selection,
                          reason: requirement.retake_replacement_reason || '',
                          relationshipLabel: 'Replacement requested from this failed attempt',
                        });
                        setRetakeReasonDialogOpen(true);
                      }}
                    >
                      {hasBothRetakeReasons ? 'View Outgoing Retake Reason' : 'View Retake Reason'}
                    </Button>
                  ) : null}
                  {isMutating ? <CircularProgress size={14} /> : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    },
    [
      canManageRequirements,
      clearanceStatusByPatientId,
      getRequirementQrCodePagePath,
      handleEvidenceDownload,
      handlePftAnalysisRetry,
      handleReviewAction,
      requirementMutationKey,
    ],
  );

  const guestTable = useMemo(() => {
    if (!guestList.length) return null;

    const columns: Array<TableColumn<AccountEventGuest>> = [];

    if (canMoveEmployees) {
      columns.push({
        label: 'Select',
        padding: 'checkbox',
        sx: { pl: 0, pr: 0.5, width: 36 },
        render: (row) => {
          const userId = row.user_id;
          return (
            <Checkbox
              checked={Boolean(userId && selectedMoveUserIds.has(userId))}
              disabled={!userId}
              inputProps={{ 'aria-label': `Select ${row.full_name || row.email}` }}
              onClick={(clickEvent) => clickEvent.stopPropagation()}
              onChange={() => toggleMoveSelection(row)}
              size="small"
              sx={{ p: 0.5 }}
            />
          );
        },
      });
    }

    columns.push({
      label: 'Name',
      sx: { pl: canMoveEmployees ? 0.5 : 1, pr: 1 },
      render: (row) => {
        const guestId = row.user_id || row.patient_id;
        return (
          <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'nowrap', gap: 1, whiteSpace: 'nowrap' }}>
            {canManageRequirements && guestId ? (
              <Tooltip title="Edit attendee details">
                <IconButton
                  size="small"
                  aria-label={`Edit details for ${row.full_name || row.email}`}
                  sx={{ p: 0.5, mr: -0.5 }}
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation();
                    handleOpenEditDrawer(row);
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <Typography variant="body2" noWrap>
              {row.full_name || '-'}
            </Typography>
            <GuestClearanceChip
              guest={row}
              orgPathName={org_path_name}
              accPathName={acc_path_name}
              eventId={event.acc_event_id}
              addAlertMessages={addAlertMessages}
            />
          </Box>
        );
      },
    });

    if (canManageRequirements) {
      columns.push({
        label: 'Requirements',
        sx: { px: 1 },
        render: (row) => {
          const requirements = row.requirements || [];
          if (!requirements.length) {
            return '-';
          }

          return (
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5 }}>
              {requirements.map((requirement) => (
                <RequirementStatusIcon key={requirement.requirement_id} requirement={requirement} />
              ))}
            </Box>
          );
        },
      });
    }

    columns.push({
      label: 'Actions',
      sx: { px: 1 },
      render: (row) => {
        const rowUserId = row.user_id || row.patient_id;
        const isAddingForm = addingRequirement?.userId === rowUserId && addingRequirement?.type === 'form';
        const isAddingTest = addingRequirement?.userId === rowUserId && addingRequirement?.type === 'physical_test';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {canManageRequirements && (
              <>
                <FormControl size="small" sx={{ minWidth: 76 }}>
                  <Select
                    value=""
                    displayEmpty
                    disabled={!rowUserId || !requirementForms.length || Boolean(addingRequirement)}
                    renderValue={() => (isAddingForm ? <CircularProgress size={14} color="inherit" /> : '+ Form')}
                    sx={{
                      height: 30,
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': {
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        px: 1,
                        py: 0.5,
                      },
                      '& .MuiSelect-icon': { fontSize: 18 },
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      const selected = requirementForms.find(
                        (form) => form.forms_catalog_id === Number(event.target.value),
                      );
                      if (selected) {
                        void handleAddRequirement(row, 'form', selected.forms_catalog_id, selected.name);
                      }
                    }}
                  >
                    {requirementForms.map((form) => (
                      <MenuItem key={form.forms_catalog_id} value={form.forms_catalog_id}>
                        {form.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 72 }}>
                  <Select
                    value=""
                    displayEmpty
                    disabled={!rowUserId || !requirementTests.length || Boolean(addingRequirement)}
                    renderValue={() => (isAddingTest ? <CircularProgress size={14} color="inherit" /> : '+ Test')}
                    sx={{
                      height: 30,
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': {
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        px: 1,
                        py: 0.5,
                      },
                      '& .MuiSelect-icon': { fontSize: 18 },
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      const selected = requirementTests.find(
                        (test) => test.tests_catalog_id === Number(event.target.value),
                      );
                      if (selected) {
                        void handleAddRequirement(row, 'physical_test', selected.tests_catalog_id, selected.name);
                      }
                    }}
                  >
                    {requirementTests.map((test) => (
                      <MenuItem key={test.tests_catalog_id} value={test.tests_catalog_id}>
                        {test.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {row.vitals ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon sx={{ fontSize: '0.95rem !important' }} />}
                    sx={{
                      width: 108,
                      minWidth: 108,
                      height: 30,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      px: 0.75,
                      py: 0.5,
                      whiteSpace: 'nowrap',
                      backgroundColor: 'rgba(46, 125, 50, 0.08)',
                      borderColor: 'rgba(46, 125, 50, 0.5)',
                      '& .MuiButton-startIcon': {
                        mr: 0.5,
                        ml: 0,
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(46, 125, 50, 0.16)',
                        borderColor: 'rgb(46, 125, 50)',
                      },
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setViewVitalsSelection(row);
                      setViewVitalsDialogOpen(true);
                    }}
                  >
                    View Vitals
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: '0.95rem !important' }} />}
                    sx={{
                      width: 108,
                      minWidth: 108,
                      height: 30,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      px: 0.75,
                      py: 0.5,
                      whiteSpace: 'nowrap',
                      '& .MuiButton-startIcon': {
                        mr: 0.5,
                        ml: 0,
                      },
                    }}
                    disabled={!row.user_id}
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsSavingVitals(false);
                      setVitalsFormKey((k) => k + 1);
                      setVitalsSelection(row);
                      setVitalsDialogOpen(true);
                    }}
                  >
                    Add Vitals
                  </Button>
                )}
              </>
            )}
            {canViewGuestDriveFolders && row.storage_folder_map?.google_drive_url && (
              <>
                <Tooltip title="Google Drive Folder">
                  <IconButton
                    component="a"
                    href={row.storage_folder_map.google_drive_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FolderSharedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Upload File to Drive">
                  <span>
                    <IconButton
                      size="small"
                      sx={{ p: 0.5 }}
                      disabled={uploadingGuestId !== null}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUploadClick(row);
                      }}
                    >
                      {uploadingGuestId === rowUserId ? (
                        <CircularProgress size={18} />
                      ) : (
                        <CloudUploadIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {row.email && (
              <Tooltip title={`Email: ${row.email}`}>
                <IconButton
                  component="a"
                  href={`mailto:${row.email}`}
                  size="small"
                  sx={{ p: 0.5 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MailOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    });

    return { columns, rows: guestList };
  }, [
    addingRequirement,
    canManageRequirements,
    canMoveEmployees,
    canViewGuestDriveFolders,
    guestList,
    handleAddRequirement,
    handleOpenEditDrawer,
    requirementForms,
    requirementTests,
    selectedMoveUserIds,
    toggleMoveSelection,
    uploadingGuestId,
    handleUploadClick,
    addAlertMessages,
    acc_path_name,
    event.acc_event_id,
    org_path_name,
  ]);

  const linkedFormsTable = useMemo(() => {
    const columns: Array<TableColumn<EventForm>> = [
      {
        label: 'Form',
        render: (row) => <Typography>{row.name}</Typography>,
      },
      {
        label: '',
        render: (row) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {row.url && (
              <>
                <Tooltip title="Copy Link">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (row.url) {
                        void navigator.clipboard.writeText(row.url);
                      }
                    }}
                    size="small"
                    sx={{ p: 0.5, mr: 1 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Show QR Code">
                  <IconButton
                    component="a"
                    href={`/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
                      acc_path_name,
                    )}/events/${encodeURIComponent(String(event.acc_event_id))}/forms/${encodeURIComponent(
                      row.form_id,
                    )}/qr-code`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ p: 0.5, mr: 1 }}
                    target="_blank"
                  >
                    <QrCode2Icon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Share via Email">
                  <IconButton
                    component="a"
                    href={`mailto:?subject=${encodeURIComponent(`Form Link: ${row.name}`)}&body=${encodeURIComponent(`Here is the link to access the form: ${row.url}`)}`}
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MailOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        ),
      },
    ];

    return { columns, rows: linkedForms };
  }, [acc_path_name, event.acc_event_id, linkedForms, org_path_name]);

  const handleFormRowClick = useCallback((row: EventForm) => {
    if (!row.url) return;
    window.open(row.url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleRowClick = useCallback(
    (row: AccountEventGuest) => {
      if (!org_path_name || !acc_path_name) return;

      const patientUserId = (row.user_id || '').trim();
      if (!patientUserId) return;

      const url = `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
        acc_path_name,
      )}/p/${encodeURIComponent(patientUserId)}`;

      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [acc_path_name, org_path_name],
  );

  return (
    <Card sx={sx}>
      <CardContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
          {/* Left Column: Location, Description, Notes */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {locationLabel || locationMapUrl ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    display: 'inline-block',
                    backgroundImage: `url(${locationIcon})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: 'contain',
                  }}
                />
                <EventDetailRow
                  label="Location"
                  value={locationLabel || '-'}
                  trailing={
                    locationMapUrl ? (
                      <>
                        {' '}
                        |{' '}
                        <Link href={locationMapUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                          Open in Maps
                        </Link>
                      </>
                    ) : null
                  }
                  sx={{ mb: 0 }}
                />
              </Box>
            ) : null}

            {event.description?.trim() ? (
              <Box sx={{ mb: 2, mt: 2 }}>{renderText('text.secondary', event.description)}</Box>
            ) : null}

            {event.notes?.trim() ? (
              <Box sx={{ mb: 2, mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Notes:
                </Typography>
                {renderText('text.secondary', event.notes)}
              </Box>
            ) : null}
          </Box>

          {/* Right Column: Staff */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, mb: 1.5 }}>
              Staff
            </Typography>
            {!assignedStaffLoaded ? (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                Loading assigned staff...
              </Typography>
            ) : assignedStaffError ? (
              <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                Unable to load assigned staff.
              </Typography>
            ) : assignedStaff.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {assignedStaff.map((row) => (
                  <Box
                    key={row.org_staff_id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {row.full_name || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {row.email ? (
                        <Tooltip title={`Email: ${row.email}`}>
                          <IconButton component="a" href={`mailto:${row.email}`} size="small" sx={{ p: 0.5 }}>
                            <MailOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {onDeleteAssignedStaff ? (
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteAssignedStaff(row);
                          }}
                        >
                          <GhostButton
                            sxProps={{
                              display: 'flex',
                              padding: '2px',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: '2px',
                              alignSelf: 'stretch',
                              '& .MuiButton-startIcon': {
                                height: '18px',
                                width: '18px',
                                backgroundImage: `url(${theme.images.deleteIcon.default})`,
                              },
                              '&:hover': {
                                background: 'transparent',
                                color: theme.buttons.ghost.hover.color,
                                border: '2px solid',
                                '& .MuiButton-startIcon': {
                                  height: '18px',
                                  width: '18px',
                                  backgroundImage: `url(${theme.images.deleteIcon.hover})`,
                                },
                              },
                            }}
                            additionalProps={{
                              style: {
                                border: 'none',
                                height: '24px',
                                minWidth: '24px',
                                padding: 0,
                                marginRight: '0px',
                              },
                              startIcon: <></>,
                            }}
                            disabled={deletingStaffId === row.org_staff_id}
                            buttonText={deletingStaffId === row.org_staff_id ? 'Removing...' : ''}
                          />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                No staff assigned.
              </Typography>
            )}

            {/* Services table */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, mb: 1.5 }}>
                Services
              </Typography>
              {servicesMetadataError ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {servicesMetadataError}
                </Alert>
              ) : null}
              {serviceCountRetries.map((retry) => (
                <Alert
                  key={retry.path}
                  severity="warning"
                  sx={{ mb: 1.5 }}
                  action={
                    <Button
                      color="inherit"
                      disabled={retryingServiceCountPath !== null}
                      onClick={() => void handleServiceCountRetry(retry)}
                      size="small"
                    >
                      {retryingServiceCountPath === retry.path ? 'Retrying…' : 'Retry'}
                    </Button>
                  }
                >
                  {retry.message}
                </Alert>
              ))}
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  backgroundColor: 'background.paper',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.8fr 1.4fr 0.8fr',
                    backgroundColor: 'background.default',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 1.25,
                    px: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Services
                  </Typography>
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Requested
                  </Typography>
                  <Typography
                    variant="caption"
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Done
                  </Typography>
                </Box>
                {servicesMetadataLoading ? (
                  <Box display="flex" justifyContent="center" sx={{ py: 3 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : servicesMetadataError && !servicesMetadata ? null : !servicesMetadata ||
                  !servicesMetadata.services_requested ||
                  servicesMetadata.services_requested.length === 0 ? (
                  <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No services requested defined for this event.
                    </Typography>
                  </Box>
                ) : (
                  servicesMetadata.services_requested.map((service, index, arr) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.8fr 1.4fr 0.8fr',
                        borderBottom: index < arr.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        py: 1.25,
                        px: 2,
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {service.name}
                      </Typography>
                      <Box display="flex" alignItems="center" justifyContent="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleQuantityChange(index, -1)}
                          disabled={serviceCountsFinalized || service.requested_count <= 0 || servicesMetadataUpdating}
                          sx={{ p: 0.25 }}
                        >
                          <RemoveIcon fontSize="small" sx={{ fontSize: '0.85rem' }} />
                        </IconButton>
                        <Typography
                          variant="body2"
                          sx={{ mx: 1, minWidth: '16px', fontWeight: 600, color: 'text.primary', textAlign: 'center' }}
                        >
                          {service.requested_count}
                        </Typography>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleQuantityChange(index, 1)}
                          disabled={serviceCountsFinalized || servicesMetadataUpdating}
                          sx={{ p: 0.25 }}
                        >
                          <AddIcon fontSize="small" sx={{ fontSize: '0.85rem' }} />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {service.done_count ?? 0}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mt: 1, mb: 3 }} />

        <Box sx={{ py: 1 }}>
          {!linkedFormsLoaded ? (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: 2, mt: 2 }}>
              Loading forms...
            </Typography>
          ) : linkedFormsError ? (
            <Typography variant="body2" color="error" sx={{ whiteSpace: 'pre-wrap', mb: 2, mt: 2 }}>
              Unable to load forms.
            </Typography>
          ) : linkedFormsTable ? (
            <Box>
              <TableCard
                title="Forms"
                rows={linkedFormsTable.rows}
                columns={linkedFormsTable.columns}
                getRowKey={(row) => row.form_id}
                onRowClick={handleFormRowClick}
                hideHeader
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                * Forms are shown based on the organization&apos;s compliance configuration.
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mb: 2, mt: 2 }}>
              No forms linked to this event.
            </Typography>
          )}
        </Box>

        {requirementActionError ? (
          <Alert severity="error" onClose={() => setRequirementActionError(null)} sx={{ mb: 2, mt: 2 }}>
            {requirementActionError}
          </Alert>
        ) : null}

        {canImportEmployees ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button
              startIcon={<CloudUploadIcon />}
              variant="outlined"
              onClick={() => employeeImportFileInputRef.current?.click()}
            >
              Add Employee from Form
            </Button>
            <input
              ref={employeeImportFileInputRef}
              hidden
              type="file"
              accept=".pdf,application/pdf"
              onChange={(inputEvent) => void handleEmployeeImportFile(inputEvent)}
            />
          </Box>
        ) : null}

        {guestTable ? (
          <TableCard
            title="Employees"
            actionButtons={
              isClearanceReportAvailable || canMoveEmployees ? (
                <Box display="flex" gap={1.5} alignItems="center">
                  {isClearanceReportAvailable ? (
                    <Button
                      disabled={isClearanceReportDownloading}
                      onClick={() => void handleClearanceReportDownload()}
                      size="small"
                      variant="outlined"
                    >
                      {isClearanceReportDownloading ? 'Downloading...' : 'Download Report'}
                    </Button>
                  ) : null}
                  {canMoveEmployees ? (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!selectedMoveUserIds.size}
                        onClick={() => void openMoveDialog()}
                      >
                        Move selected
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={!selectedMoveUserIds.size}
                        onClick={() => setRemoveDialogOpen(true)}
                      >
                        Remove selected
                      </Button>
                    </>
                  ) : null}
                </Box>
              ) : undefined
            }
            rows={guestTable.rows}
            columns={guestTable.columns}
            getRowKey={(row, i) => `${(row.user_id || row.email || `attendee-${i}`).toLowerCase()}-${i}`}
            onRowClick={handleRowClick}
            renderExpandedRow={canManageRequirements ? renderExpandedRequirements : undefined}
            hideHeader
            getRowSx={(row) => {
              if (!areGuestRequirementsCompleted(row)) {
                return undefined;
              }

              return {
                backgroundColor: 'rgba(46, 125, 50, 0.06)',
                '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.1)' },
              };
            }}
          />
        ) : guestList.length ? (
          <EventDetailRow
            label="Employees"
            value={guestList
              .map((guest) => (guest.full_name ? `${guest.full_name} (${guest.email})` : guest.email))
              .join(', ')}
            sx={{ whiteSpace: 'pre-wrap', mb: 2 }}
          />
        ) : null}

        <Drawer
          anchor="right"
          open={Boolean(editGuest)}
          onClose={handleCloseEditDrawer}
          PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, p: 3 } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Attendee Details
            </Typography>
            <IconButton onClick={handleCloseEditDrawer} disabled={isSavingEdit}>
              <CloseIcon />
            </IconButton>
          </Box>
          {editError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {editError}
            </Alert>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              autoFocus
              required
              fullWidth
              label="First name"
              value={editFirstName}
              disabled={isSavingEdit}
              inputProps={{ maxLength: 100 }}
              onChange={(event) => setEditFirstName(event.target.value)}
            />
            <TextField
              fullWidth
              label="Last name"
              value={editLastName}
              disabled={isSavingEdit}
              inputProps={{ maxLength: 100 }}
              onChange={(event) => setEditLastName(event.target.value)}
            />
            <TextField
              required
              fullWidth
              label="Email address"
              type="email"
              value={editEmail}
              disabled={isSavingEdit}
              inputProps={{ maxLength: 320 }}
              onChange={(event) => setEditEmail(event.target.value)}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 4 }}>
            <Button onClick={handleCloseEditDrawer} disabled={isSavingEdit} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSaveEdit()}
              disabled={isSavingEdit || !editFirstName.trim() || !editEmail.trim()}
            >
              {isSavingEdit ? <CircularProgress size={20} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        </Drawer>

        <Dialog
          open={moveDialogOpen}
          onClose={() => {
            if (!moveSubmitting) setMoveDialogOpen(false);
          }}
          fullWidth
          maxWidth="sm"
          TransitionProps={{
            onExited: () => {
              if (!resetMoveStateAfterExitRef.current) return;
              resetMoveStateAfterExitRef.current = false;
              setSelectedMoveUserIds(new Set());
              setMoveTargetEventId('');
              setMoveTargetEvents([]);
              setMoveError(null);
            },
          }}
        >
          <DialogTitle>Move employees</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Move {selectedMoveUserIds.size} selected employee{selectedMoveUserIds.size === 1 ? '' : 's'} to another
              active or upcoming event. Requirements and the employee Drive folder will move with them.
            </Typography>
            {moveError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {moveError}
              </Alert>
            ) : null}
            <TextField
              select
              fullWidth
              label="Destination event"
              value={moveTargetEventId}
              onChange={(changeEvent) => setMoveTargetEventId(Number(changeEvent.target.value))}
              disabled={moveTargetsLoading || moveSubmitting}
            >
              {moveTargetEvents.map((candidate) => (
                <MenuItem key={candidate.acc_event_id} value={candidate.acc_event_id}>
                  {candidate.title || 'Untitled event'} —{' '}
                  {formatEventDateTime(candidate.event_start_date, candidate.event_end_date)}
                </MenuItem>
              ))}
            </TextField>
            {!moveTargetsLoading && !moveTargetEvents.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No active or upcoming destination events are available.
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMoveDialogOpen(false)} disabled={moveSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleMoveEmployees()}
              disabled={!moveTargetEventId || moveTargetsLoading || moveSubmitting}
            >
              {moveSubmitting ? 'Moving…' : 'Move employees'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={removeDialogOpen} onClose={closeRemoveDialog} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Remove Employees from Event</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Are you sure you want to remove the selected employee(s) from this event? This will disassociate their
              compliance checklists from this event. Any submitted questionnaires and files will remain safely archived.
            </Typography>
            {removeError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {removeError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button variant="text" color="inherit" disabled={removeSubmitting} onClick={closeRemoveDialog}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={removeSubmitting}
              onClick={() => void handleRemoveEmployees()}
              startIcon={removeSubmitting ? <CircularProgress size={20} color="inherit" /> : <DeleteOutlineIcon />}
            >
              {removeSubmitting ? 'Removing...' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={employeeImportDialogOpen}
          onClose={() => {
            if (employeeImportStatus !== 'extracting' && employeeImportStatus !== 'confirming') {
              resetEmployeeImport();
            }
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Add Employee from Respiratory Form</DialogTitle>
          <DialogContent>
            {employeeImportStatus === 'extracting' ? (
              <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 2, py: 4 }}>
                <CircularProgress />
                <Typography>Classifying the form and extracting employee details…</Typography>
                <Typography variant="caption" color="text.secondary">
                  No account or patient will be created during this step.
                </Typography>
              </Box>
            ) : null}

            {employeeImportStatus === 'error' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                <Alert severity="error">{employeeImportError || 'Unable to process this PDF.'}</Alert>
                <Typography variant="body2" color="text.secondary">
                  No employee account was created during document extraction.
                </Typography>
              </Box>
            ) : null}

            {employeeImportStatus === 'review' || employeeImportStatus === 'confirming' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {employeeImportError ? <Alert severity="error">{employeeImportError}</Alert> : null}
                {employeeImportAccountIdentityConflict ? (
                  <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                      <Checkbox
                        checked={employeeImportUseConfirmedIdentity}
                        color="warning"
                        disabled={employeeImportStatus === 'confirming'}
                        inputProps={{
                          'aria-label': 'Use the confirmed form details to update an existing account',
                        }}
                        onChange={(changeEvent) => setEmployeeImportUseConfirmedIdentity(changeEvent.target.checked)}
                        size="small"
                      />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Apply the confirmed details above to the existing account and its matching patient record.
                      </Typography>
                    </Box>
                  </Alert>
                ) : null}
                {employeeImportPreview?.document_completeness === 'PARTIAL' ? (
                  <Alert severity="warning">
                    This is a partial form. Only details visible on the supplied pages were extracted.
                  </Alert>
                ) : null}
                {employeeImportPreview?.warnings?.length ? (
                  <Alert severity="warning">{employeeImportPreview.warnings.join(' ')}</Alert>
                ) : null}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip color="success" label="Respiratory form matched" size="small" />
                  <Typography variant="caption" color="text.secondary">
                    Confidence: {employeeImportPreview?.classification.confidence_score?.toFixed(1)}%
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Review and correct every value. Gender and email must be explicitly confirmed before creation.
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  }}
                >
                  <TextField
                    required
                    label="First name"
                    value={employeeImportValues.firstName}
                    error={Boolean(employeeImportFieldErrors.firstName)}
                    helperText={employeeImportFieldErrors.firstName}
                    disabled={employeeImportStatus === 'confirming'}
                    onChange={(changeEvent) => {
                      setEmployeeImportValues((values) => ({ ...values, firstName: changeEvent.target.value }));
                      setEmployeeImportFieldErrors((errors) => ({ ...errors, firstName: undefined }));
                    }}
                  />
                  <TextField
                    required
                    label="Last name"
                    value={employeeImportValues.lastName}
                    error={Boolean(employeeImportFieldErrors.lastName)}
                    helperText={employeeImportFieldErrors.lastName}
                    disabled={employeeImportStatus === 'confirming'}
                    onChange={(changeEvent) => {
                      setEmployeeImportValues((values) => ({ ...values, lastName: changeEvent.target.value }));
                      setEmployeeImportFieldErrors((errors) => ({ ...errors, lastName: undefined }));
                    }}
                  />
                  <TextField
                    required
                    type="date"
                    label="Birthdate"
                    value={employeeImportValues.birthdate}
                    error={Boolean(employeeImportFieldErrors.birthdate)}
                    helperText={employeeImportFieldErrors.birthdate}
                    disabled={employeeImportStatus === 'confirming'}
                    InputLabelProps={{ shrink: true }}
                    onChange={(changeEvent) => {
                      setEmployeeImportValues((values) => ({ ...values, birthdate: changeEvent.target.value }));
                      setEmployeeImportFieldErrors((errors) => ({ ...errors, birthdate: undefined }));
                    }}
                  />
                  <TextField
                    required
                    label="Phone number"
                    value={employeeImportValues.phoneNumber}
                    error={Boolean(employeeImportFieldErrors.phoneNumber)}
                    helperText={employeeImportFieldErrors.phoneNumber}
                    disabled={employeeImportStatus === 'confirming'}
                    onChange={(changeEvent) => {
                      setEmployeeImportValues((values) => ({ ...values, phoneNumber: changeEvent.target.value }));
                      setEmployeeImportFieldErrors((errors) => ({ ...errors, phoneNumber: undefined }));
                    }}
                  />
                  <TextField
                    required
                    select
                    label="Gender"
                    value={employeeImportValues.gender}
                    error={Boolean(employeeImportFieldErrors.gender)}
                    disabled={employeeImportStatus === 'confirming'}
                    helperText={
                      employeeImportFieldErrors.gender ||
                      (employeeImportPreview?.employee.gender
                        ? `Detected from form: ${employeeImportPreview.employee.gender}. Please confirm.`
                        : 'Required. Please select a value.')
                    }
                    onChange={(changeEvent) => {
                      setEmployeeImportValues((values) => ({
                        ...values,
                        gender: changeEvent.target.value as EmployeeImportValues['gender'],
                      }));
                      setEmployeeImportFieldErrors((errors) => ({ ...errors, gender: undefined }));
                    }}
                  >
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="unknown">Unknown</MenuItem>
                  </TextField>
                  <TextField
                    required
                    type="email"
                    label="Email"
                    value={employeeImportValues.email}
                    error={Boolean(employeeImportFieldErrors.email)}
                    helperText={employeeImportFieldErrors.email || 'Confirm this address before creating the account.'}
                    disabled={employeeImportStatus === 'confirming'}
                    onChange={(changeEvent) => {
                      setEmployeeImportValues((values) => ({ ...values, email: changeEvent.target.value }));
                      setEmployeeImportFieldErrors((errors) => ({ ...errors, email: undefined }));
                    }}
                  />
                </Box>
                <Alert severity="info">
                  Confirming creates or reuses the user, adds the user as an account member, and adds the user to this
                  event.
                </Alert>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={employeeImportStatus === 'extracting' || employeeImportStatus === 'confirming'}
              onClick={resetEmployeeImport}
            >
              Close
            </Button>
            {employeeImportStatus === 'error' && employeeImportErrorAction ? (
              <Button
                variant={employeeImportErrorAction === 'choose-file' ? 'contained' : 'outlined'}
                onClick={() => {
                  if (employeeImportErrorAction === 'choose-file') {
                    employeeImportFileInputRef.current?.click();
                  } else if (employeeImportFile) {
                    void handleEmployeeImportPreview(employeeImportFile);
                  }
                }}
              >
                {employeeImportErrorAction === 'choose-file'
                  ? 'Choose Another PDF'
                  : employeeImportErrorAction === 'reprocess'
                    ? 'Extract Again'
                    : 'Retry Extraction'}
              </Button>
            ) : null}
            {employeeImportStatus === 'error' &&
            employeeImportErrorAction &&
            employeeImportErrorAction !== 'choose-file' ? (
              <Button onClick={() => employeeImportFileInputRef.current?.click()}>Choose Another PDF</Button>
            ) : null}
            {employeeImportStatus === 'review' || employeeImportStatus === 'confirming' ? (
              <Button
                variant="contained"
                disabled={employeeImportStatus === 'confirming'}
                onClick={() => void handleEmployeeImportConfirm()}
              >
                {employeeImportStatus === 'confirming' ? 'Creating…' : 'Confirm and Create'}
              </Button>
            ) : null}
          </DialogActions>
        </Dialog>

        <Dialog
          open={uploadDialogOpen}
          onClose={() => {
            if (!requirementMutationKey) {
              setUploadDialogOpen(false);
            }
          }}
          TransitionProps={{
            onExited: () => {
              if (!uploadDialogOpen) {
                setUploadSelection(null);
                setSelectedUploadFile(null);
                setIsPftReupload(false);
              }
            },
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{isPftReupload ? 'Reupload PFT PDF' : 'Upload Requirement File'}</DialogTitle>
          <DialogContent>
            {requirementActionError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {requirementActionError}
              </Alert>
            ) : null}
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Patient: {uploadSelection?.guest.full_name || uploadSelection?.guest.email}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Requirement: {uploadSelection?.requirement.attendee_req_name}
            </Typography>
            <Button component="label" variant="outlined">
              Choose File
              <input
                hidden
                type="file"
                accept={
                  isPftReupload
                    ? '.pdf,application/pdf'
                    : '.pdf,.jpeg,.jpg,.png,application/pdf,image/jpeg,image/png'
                }
                onChange={(event) => setSelectedUploadFile(event.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedUploadFile?.name || (isPftReupload ? 'PDF up to 10 MB' : 'PDF, JPEG, JPG, or PNG up to 10 MB')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={Boolean(requirementMutationKey)}
              onClick={() => {
                setUploadDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!selectedUploadFile || Boolean(requirementMutationKey)}
              onClick={() => void handleRequirementUpload()}
            >
              {requirementMutationKey?.endsWith(':pft-reupload')
                ? 'Reuploading...'
                : requirementMutationKey?.endsWith(':upload')
                  ? 'Uploading...'
                  : isPftReupload
                    ? 'Reupload PFT PDF'
                    : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={failureDialogOpen}
          onClose={() => {
            if (!requirementMutationKey) {
              setFailureDialogOpen(false);
            }
          }}
          TransitionProps={{
            onExited: () => {
              if (!failureDialogOpen) {
                setFailureSelection(null);
                setFailureComment('');
              }
            },
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Fail Requirement</DialogTitle>
          <DialogContent>
            {requirementActionError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {requirementActionError}
              </Alert>
            ) : null}
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Patient: {failureSelection?.guest.full_name || failureSelection?.guest.email}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Requirement: {failureSelection?.requirement.attendee_req_name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Current Status:{' '}
              {failureSelection ? REQUIREMENT_STATUS_CONFIG[failureSelection.requirement.status].label : '—'}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              required
              label="Reason"
              value={failureComment}
              onChange={(event) => setFailureComment(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              disabled={Boolean(requirementMutationKey)}
              onClick={() => {
                setFailureDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={!failureComment.trim() || Boolean(requirementMutationKey)}
              onClick={() => {
                if (failureSelection) {
                  void handleReviewAction(failureSelection, 'FAILED', failureComment);
                }
              }}
            >
              {requirementMutationKey?.endsWith(':FAILED') ? 'Failing...' : 'Fail Requirement'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={retakeDialogOpen}
          onClose={() => {
            if (!requirementMutationKey) {
              setRetakeDialogOpen(false);
            }
          }}
          TransitionProps={{
            onExited: () => {
              if (!retakeDialogOpen) {
                setRetakeSelection(null);
                setRetakeReasonCode('');
                setRetakeOtherComment('');
              }
            },
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Request Requirement Retake</DialogTitle>
          <DialogContent>
            {requirementActionError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {requirementActionError}
              </Alert>
            ) : null}
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Patient: {retakeSelection?.guest.full_name || retakeSelection?.guest.email}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Requirement: {retakeSelection?.requirement.attendee_req_name}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              select
              required
              label="Retake reason"
              value={retakeReasonCode}
              onChange={(event) => {
                setRetakeReasonCode(event.target.value);
                setRetakeOtherComment('');
              }}
            >
              {(retakeSelection?.requirement.retake_reasons || []).map((reason) => (
                <MenuItem key={reason.code} value={reason.code}>
                  {reason.label}
                </MenuItem>
              ))}
            </TextField>
            {selectedRetakeReason?.requires_comment ? (
              <TextField
                fullWidth
                multiline
                minRows={3}
                required
                label="Explanation"
                sx={{ mt: 2 }}
                value={retakeOtherComment}
                onChange={(event) => setRetakeOtherComment(event.target.value)}
              />
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={Boolean(requirementMutationKey)}
              onClick={() => {
                setRetakeDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              color="warning"
              variant="contained"
              disabled={
                !retakeReasonCode ||
                (selectedRetakeReason?.requires_comment && !retakeOtherComment.trim()) ||
                Boolean(requirementMutationKey)
              }
              onClick={() => {
                if (retakeSelection) {
                  void handleReviewAction(retakeSelection, 'RETAKE_REQUESTED', undefined, {
                    reasonCode: retakeReasonCode,
                    otherComment: retakeOtherComment,
                  });
                }
              }}
            >
              {requirementMutationKey?.endsWith(':RETAKE_REQUESTED') ? 'Requesting...' : 'Request Retake'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={commentDialogOpen}
          onClose={() => setCommentDialogOpen(false)}
          TransitionProps={{
            onExited: () => {
              if (!commentDialogOpen) {
                setCommentSelection(null);
              }
            },
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Failure Comment</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Patient: {commentSelection?.guest.full_name || commentSelection?.guest.email}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Requirement: {commentSelection?.requirement.attendee_req_name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Failed By:{' '}
              {commentSelection?.requirement.latest_failure?.performed_by_user_name ||
                commentSelection?.requirement.latest_failure?.performed_by_user_id ||
                '-'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Failed At:{' '}
              {commentSelection?.requirement.latest_failure?.created_at
                ? formatDateTime(commentSelection.requirement.latest_failure.created_at, {
                    month: 'long',
                    day: 'numeric',
                  })
                : '-'}
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Comment
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {commentSelection?.requirement.latest_failure?.comment || '-'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCommentDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={retakeReasonDialogOpen}
          onClose={() => setRetakeReasonDialogOpen(false)}
          TransitionProps={{
            onExited: () => {
              if (!retakeReasonDialogOpen) {
                setRetakeReasonSelection(null);
              }
            },
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Retake Reason</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Patient: {retakeReasonSelection?.guest.full_name || retakeReasonSelection?.guest.email}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Requirement: {retakeReasonSelection?.requirement.attendee_req_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {retakeReasonSelection?.relationshipLabel}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {retakeReasonSelection?.reason || '-'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRetakeReasonDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={vitalsDialogOpen}
          onClose={() => {
            if (isSavingVitals) {
              return;
            }
            setVitalsDialogOpen(false);
          }}
          TransitionProps={{
            onExited: () => {
              if (!vitalsDialogOpen) {
                setVitalsSelection(null);
                setIsSavingVitals(false);
              }
            },
          }}
          disableEscapeKeyDown={isSavingVitals}
          fullWidth
          maxWidth="sm"
        >
          {vitalsSelection?.user_id && (
            <AddVitalsForm
              key={vitalsFormKey}
              orgPathName={org_path_name}
              accPathName={acc_path_name}
              patientUserId={vitalsSelection.user_id}
              eventId={event.acc_event_id}
              isDialog={true}
              onSubmittingChange={setIsSavingVitals}
              onSuccess={async () => {
                setIsSavingVitals(false);
                setVitalsDialogOpen(false);
                addAlertMessages({ message: 'Vitals saved successfully', severityType: 'success' });
                await onRequirementsUpdated?.();
              }}
              onCancel={() => {
                setIsSavingVitals(false);
                setVitalsDialogOpen(false);
              }}
            />
          )}
        </Dialog>

        <Dialog
          open={viewVitalsDialogOpen}
          onClose={() => setViewVitalsDialogOpen(false)}
          TransitionProps={{
            onExited: () => {
              if (!viewVitalsDialogOpen) {
                setViewVitalsSelection(null);
              }
            },
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" component="div">
                Vitals
              </Typography>
              <IconButton size="small" onClick={() => setViewVitalsDialogOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {viewVitalsSelection && (
              <Typography variant="body2" color="text.secondary">
                {viewVitalsSelection.full_name || viewVitalsSelection.email}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent dividers>
            {viewVitalsSelection?.vitals ? (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Blood Pressure
                  </Typography>
                  <Typography variant="h5" fontWeight={600} color="success.main">
                    {viewVitalsSelection.vitals.systolic}/{viewVitalsSelection.vitals.diastolic}{' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      mmHg
                    </Typography>
                  </Typography>
                </Box>
                {viewVitalsSelection.vitals.bpm != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Heart Rate
                    </Typography>
                    <Typography variant="h6" fontWeight={500}>
                      {viewVitalsSelection.vitals.bpm}{' '}
                      <Typography component="span" variant="body2" color="text.secondary">
                        bpm
                      </Typography>
                    </Typography>
                  </Box>
                )}
                {viewVitalsSelection.vitals.recorded_at && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Recorded At
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(viewVitalsSelection.vitals.recorded_at, {
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No vitals recorded for this event.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
            {viewVitalsSelection?.user_id && org_path_name && acc_path_name ? (
              <Button
                size="small"
                onClick={() => {
                  const url = `/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
                    acc_path_name,
                  )}/p/${encodeURIComponent(viewVitalsSelection.user_id || '')}?tab=vitals`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
                sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
              >
                View Patient Record ↗
              </Button>
            ) : <Box />}
            <Button onClick={() => setViewVitalsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Centered Employee Document Upload & Verification Modal */}
        <Dialog
          open={employeeUploadModalOpen}
          onClose={() => {
            // Only allow closing manually if we are in success or error state
            if (employeeUploadStatus === 'success' || employeeUploadStatus === 'error') {
              setEmployeeUploadModalOpen(false);
            }
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: { borderRadius: '16px', padding: '24px' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2 }}>
            {employeeUploadStatus === 'uploading' && (
              <>
                <CircularProgress size={48} sx={{ mb: 3 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Uploading Document
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Uploading "{employeeUploadFile?.name}" for <strong>{uploadTargetEmployeeName}</strong>...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This includes secure storage and AI identity verification. Please do not close this window.
                </Typography>
              </>
            )}

            {employeeUploadStatus === 'success' && (
              <>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'success.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    color: 'success.dark',
                  }}
                >
                  <AssignmentTurnedInOutlinedIcon style={{ fontSize: 32 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'success.dark' }}>
                  Upload & Verification Succeeded!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  "{employeeUploadFile?.name}" has been successfully verified, matched, and uploaded.
                </Typography>
              </>
            )}

            {employeeUploadStatus === 'error' && (
              <>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: 'error.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    color: 'error.dark',
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 32 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'error.dark' }}>
                  Verification Failed
                </Typography>
                <Box
                  sx={{
                    bgcolor: 'background.default',
                    p: 2,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                    width: '100%',
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ textAlign: 'left', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
                  >
                    {employeeUploadError || EMPLOYEE_UPLOAD_ERROR_MESSAGE}
                  </Typography>
                </Box>
                <BorderedButton onClick={() => setEmployeeUploadModalOpen(false)} buttonText="Close" />
              </>
            )}
          </Box>
        </Dialog>

        {renderSnackBarAlertComponent()}
      </CardContent>
      {/* Hidden native file input for uploading files to guest drive folders */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
    </Card>
  );
}
